import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { ContextSession } from "../../services/ContextSession";
import {
  apiRequest,
  buildApiUrl,
  buildActualizarPayload,
  getAccessToken,
  getUltimaFotoPersona,
  mapPersonaPayloadToForm,
  subirFotoPersona,
  toIsoDate,
} from "./enrolamientoApi";
import {
  formatIdentificacionVisual,
  formatTelefonoVisual,
  stripIdentificacionVisual,
} from "./identificacionUtils";

const topRequired = [
  "tipoDocumento",
  "identificacion",
  "primerNombre",
  "primerApellido",
  "fechaNacimiento",
  "paisNacimiento",
  "nacionalidad",
  "genero",
  "estadoCivil",
  "lugarNacimiento",
  "profesion",
  "actividadEconomicaCnbs",
  "profesionCnbs",
  "nivelEducativo",
  "tipoCliente",
];

const laboralRequired = [
  "patrono",
  "departamentoTrabajo",
  "municipioTrabajo",
  "coloniaTrabajo",
  "fechaIngresoInstitucion",
  "antiguedadLaboral",
  "ingresoAproxRango",
  "ingresoAproxMonto",
  "giroEntidad",
  "cargoDesempenado",
  "telefonoTrabajo",
];

const baseHeaders = [
  "RESIDENCIAL",
  "LABORAL",
  "PATRIMONIO",
  "BENEFICIARIOS",
  "REFERENCIAS",
  "OTROS",
];

const UI_PRIMARY = "#19498c";
const UI_GOLD = "#AD8401";
const DEFAULT_PERFIL_LAFT = "A";
const MAX_TIEMPO_RESIDIR = 150;
const _ONLY_LETTERS = /[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g;
const _ONLY_LETTERS_NUMS = /[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s]/g;
const _ONLY_DIGITS = /\D/g;
const _NAME_FIELDS = ["primerNombre","segundoNombre","primerApellido","segundoApellido","nombreConyuge"];
const _NUM_FIELDS = ["telefono","movil","rtn","identificacionConyuge","telefonoTrabajo"];
const _UPPER_FIELDS = [
  "patrono",
  "lugarNacimiento",
  "coloniaResidencia",
  "direccionCompleta",
  "calle",
  "numeroCasa",
  "referenciaDireccion",
  "direccionTrabajo",
  "numeroCasaTrabajo",
  "referenciaTrabajo",
  "areaTrabajo",
  "descOtrosInmuebles",
  "descOtrosIngresos",
];
const _EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const IDENTIFICACION_FIELDS = new Set(["identificacion", "identificacionConyuge"]);
const TELEFONO_FIELDS = new Set(["telefono", "movil", "telefonoTrabajo"]);
const REQUIRED_FIELD_LABELS = {
  tipoDocumento: "Tipo de documento",
  identificacion: "Identificación",
  primerNombre: "Primer nombre",
  primerApellido: "Primer apellido",
  fechaNacimiento: "Fecha de nacimiento",
  paisNacimiento: "País de nacimiento",
  nacionalidad: "Nacionalidad",
  genero: "Género",
  estadoCivil: "Estado civil",
  lugarNacimiento: "Lugar de nacimiento",
  profesion: "Profesión",
  actividadEconomicaCnbs: "Actividad Económica CNBS",
  profesionCnbs: "Profesión CNBS",
  nivelEducativo: "Nivel educativo",
  tipoCliente: "Tipo de cliente",
  departamentoResidencia: "Departamento residencia",
  municipioResidencia: "Municipio residencia",
  coloniaResidencia: "Colonia residencia",
  direccionCompleta: "Dirección completa",
  tiempoResidir: "Tiempo de residir",
  fechaDesdeReside: "Fecha desde que reside",
  telefono: "Número de teléfono",
  movil: "Número de celular",
  patrono: "Patrono",
  departamentoTrabajo: "Departamento trabajo",
  municipioTrabajo: "Municipio trabajo",
  coloniaTrabajo: "Colonia trabajo",
  fechaIngresoInstitucion: "Fecha ingreso institución",
  antiguedadLaboral: "Antigüedad laboral",
  ingresoAproxRango: "Ingreso aproximado (rango)",
  ingresoAproxMonto: "Ingreso aproximado (monto)",
  giroEntidad: "Giro de la entidad",
  cargoDesempenado: "Cargo desempeñado",
  telefonoTrabajo: "Teléfono de trabajo",
  tipoVivienda: "Tipo de vivienda",
  numeroVehiculos: "Número de vehículos",
  descOtrosInmuebles: "Descripción de otros inmuebles",
  descOtrosIngresos: "Descripción de otros ingresos",
  montoOtrosIngresos: "Monto de otros ingresos",
  perfilLaft: "Perfil LAFT",
  correo: "Correo electrónico",
};
const emptyForm = {
  personaIdInterno: "",
  tipoDocumento: "",
  identificacion: "",
  primerNombre: "",
  segundoNombre: "",
  primerApellido: "",
  segundoApellido: "",
  fechaNacimiento: "",
  paisNacimiento: "",
  nacionalidad: "",
  genero: "",
  estadoCivil: "",
  rtn: "",
  lugarNacimiento: "",
  profesion: "",
  actividadEconomicaCnbs: "",
  profesionCnbs: "",
  nivelEducativo: "",
  tipoCliente: "",
  dependientesEconomicos: "",
  esPep: false,
  departamentoResidencia: "",
  municipioResidencia: "",
  coloniaResidencia: "",
  direccionCompleta: "",
  calle: "",
  numeroCasa: "",
  referenciaDireccion: "",
  telefono: "",
  movil: "",
  correo: "",
  tiempoResidir: "",
  fechaDesdeReside: "",
  patrono: "",
  departamentoTrabajo: "",
  municipioTrabajo: "",
  coloniaTrabajo: "",
  direccionTrabajo: "",
  numeroCasaTrabajo: "",
  referenciaTrabajo: "",
  fechaIngresoInstitucion: "",
  antiguedadLaboral: "",
  ingresoAproxRango: "",
  ingresoAproxMonto: "",
  giroEntidad: "",
  cargoDesempenado: "",
  areaTrabajo: "",
  telefonoTrabajo: "",
  tipoVivienda: "",
  propiedadesPersonales: false,
  poseeVehiculo: false,
  numeroVehiculos: "",
  poseeInmuebles: false,
  descOtrosInmuebles: "",
  ingresosPersonales: false,
  otrosIngresos: false,
  descOtrosIngresos: "",
  montoOtrosIngresos: "",
  perfilLaft: DEFAULT_PERFIL_LAFT,
  tipoDocumentoConyuge: "",
  identificacionConyuge: "",
  nombreConyuge: "",
  fechaNacimientoConyuge: "",
  generoConyuge: "",
  estadoHuellas: "",
  registroAfis: "",
  transaccionOrigen: "",
  observacionesPersona: "",
  observacionHuellas: "",
  userIdRegistro: "",
  userRegistroNombre: "",
  oficinaIdRegistro: "",
  oficinaIdRegistroNombre: "",
  userIdActualizo: "",
  userActualizoNombre: "",
};

const emptyCatalogos = {
  documentoTipos: [],
  estadosCiviles: [],
  sexos: [],
  nacionalidades: [],
  nivelesEducativos: [],
  perfilesLaft: [],
  profesiones: [],
  actividadesEconomicasCnbs: [],
  profesionesCnbs: [],
  viviendaTipos: [],
  ingresosAproximados: [],
  girosEntidad: [],
  cargos: [],
  departamentos: [],
  municipios: [],
  colonias: [],
  parentescos: [],
  tipoClientes: [],
  referenciaTipos: [],
  padecimientos: [],
};

const getNacionalidadDescripcion = (item) =>
  String(item?.Descripcion ?? "").trim();
const getNacionalidadGentilicio = (item) => {
  const gentilicio = String(
    item?.GentilicioDisplay ?? item?.Gentilicio ?? "",
  ).trim();
  return gentilicio || getNacionalidadDescripcion(item);
};
const normalizeNacionalidadesCatalog = (rows = []) =>
  (rows || []).map((item) => ({
    ...item,
    GentilicioDisplay: getNacionalidadGentilicio(item),
  }));
const sortCatalogById = (rows = [], idKey = "id") =>
  [...(rows || [])].sort((a, b) => {
    const aRaw = String(a?.[idKey] ?? "").trim();
    const bRaw = String(b?.[idKey] ?? "").trim();
    const aNum = Number(aRaw);
    const bNum = Number(bRaw);
    const aIsNum = Number.isFinite(aNum);
    const bIsNum = Number.isFinite(bNum);
    if (aIsNum && bIsNum) return aNum - bNum;
    if (aIsNum) return -1;
    if (bIsNum) return 1;
    return aRaw.localeCompare(bRaw, "es", {
      numeric: true,
      sensitivity: "base",
    });
  });

export default function ActualizaDatosAfiliacion({
  includePerfilLaft = false,
}) {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";
  const [tab, setTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [missingRequiredFields, setMissingRequiredFields] = useState([]);
  const [ok, setOk] = useState("");
  const [dniBusqueda, setDniBusqueda] = useState("");
  const [primerNombreBusqueda, setPrimerNombreBusqueda] = useState("");
  const [primerApellidoBusqueda, setPrimerApellidoBusqueda] = useState("");
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [fotoPersonaUrl, setFotoPersonaUrl] = useState("");
  const [fechaActualizacionInfo, setFechaActualizacionInfo] = useState("");
  const [diasTranscurridosInfo, setDiasTranscurridosInfo] = useState("");
  const [catalogos, setCatalogos] = useState({ ...emptyCatalogos });
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);

  const [beneficiarios, setBeneficiarios] = useState([]);
  const [referencias, setReferencias] = useState([]);
  const [padecimientos, setPadecimientos] = useState([]);
  const [openBeneficiario, setOpenBeneficiario] = useState(false);
  const [openReferencia, setOpenReferencia] = useState(false);
  const [openPadecimiento, setOpenPadecimiento] = useState(false);
  const [editBeneficiarioId, setEditBeneficiarioId] = useState(null);
  const [editReferenciaId, setEditReferenciaId] = useState(null);
  const [editPadecimientoId, setEditPadecimientoId] = useState(null);
  const [beneficiarioErrors, setBeneficiarioErrors] = useState({});
  const [referenciaErrors, setReferenciaErrors] = useState({});
  const [padecimientoError, setPadecimientoError] = useState("");
  const [beneficiarioForm, setBeneficiarioForm] = useState({
    beneficiarioId: null,
    dni: "",
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    parentesco: "",
    tipoDocumento: "",
    porcentaje: "0",
    FKDesignacionId: null,
  });
  const [referenciaForm, setReferenciaForm] = useState({
    referenciaId: null,
    nombreCompleto: "",
    telefono: "",
    direccion: "",
    tipoReferencia: "",
    FKReferenciaTipoId: "",
  });
  const [padecimientoForm, setPadecimientoForm] = useState({
    id: "",
    descripcion: "",
  });
  const [familiaresPpe, setFamiliaresPpe] = useState([]);
  const [empresasPpe, setEmpresasPpe] = useState([]);
  const [openFamiliar, setOpenFamiliar] = useState(false);
  const [openEmpresa, setOpenEmpresa] = useState(false);
  const [editFamiliarId, setEditFamiliarId] = useState(null);
  const [editEmpresaId, setEditEmpresaId] = useState(null);
  const [familiarErrors, setFamiliarErrors] = useState({});
  const [empresaErrors, setEmpresaErrors] = useState({});
  const [familiarForm, setFamiliarForm] = useState({
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    parentesco: "",
    nacionalidad: "",
  });
  const [empresaForm, setEmpresaForm] = useState({
    razonSocial: "",
    rtn: "",
    pais: "",
    giroEntidad: "",
  });
  const [adjuntos, setAdjuntos] = useState([]);
  const [adjuntoFile, setAdjuntoFile] = useState(null);
  const [adjuntoDescripcion, setAdjuntoDescripcion] = useState("");
  const [cargandoAdjuntos, setCargandoAdjuntos] = useState(false);
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false);
  const [openAdjuntoPreview, setOpenAdjuntoPreview] = useState(false);
  const [previewAdjuntoUrl, setPreviewAdjuntoUrl] = useState("");
  const [previewAdjuntoMime, setPreviewAdjuntoMime] = useState("");
  const [previewAdjuntoError, setPreviewAdjuntoError] = useState("");
  const [previewAdjuntoLoading, setPreviewAdjuntoLoading] = useState(false);
  const adjuntoInputRef = useRef(null);
  const fotoInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotoError, setFotoError] = useState("");
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState("");
  const [capturedPhotoUrl, setCapturedPhotoUrl] = useState("");

  const totalBeneficiario = useMemo(
    () => (beneficiarios || []).length,
    [beneficiarios],
  );
  const headers = useMemo(() => {
    const nextHeaders = [...baseHeaders];
    if (includePerfilLaft && !nextHeaders.includes("PERFIL LAFT")) {
      const otrosIndex = nextHeaders.indexOf("OTROS");
      if (otrosIndex < 0) nextHeaders.push("PERFIL LAFT");
      else nextHeaders.splice(otrosIndex, 0, "PERFIL LAFT");
    }
    if (!form.esPep) return nextHeaders;
    const otrosIndex = nextHeaders.indexOf("OTROS");
    if (otrosIndex < 0) return [...nextHeaders, "PPE"];
    return [
      ...nextHeaders.slice(0, otrosIndex),
      "PPE",
      ...nextHeaders.slice(otrosIndex),
    ];
  }, [form.esPep, includePerfilLaft]);
  const activeHeader = headers[tab] || headers[0];
  useEffect(() => {
    if (tab > headers.length - 1) setTab(0);
  }, [headers, tab]);

  useEffect(
    () => () => {
      if (previewAdjuntoUrl) {
        URL.revokeObjectURL(previewAdjuntoUrl);
      }
    },
    [previewAdjuntoUrl],
  );

  useEffect(() => {
    let active = true;
    const loadCatalogos = async () => {
      if (!authToken) return;
      try {
        setLoadingCatalogos(true);
        const data = await apiRequest("/Enrolamiento/catalogos/", {
          authToken,
        });
        if (!active) return;
        const c = data?.catalogos || {};
        setCatalogos({
          documentoTipos: c?.documento_tipos || [],
          estadosCiviles: c?.estados_civiles || [],
          sexos: c?.sexos || [],
          nacionalidades: normalizeNacionalidadesCatalog(
            c?.nacionalidades || [],
          ),
          nivelesEducativos: sortCatalogById(
            c?.niveles_educativos || [],
            "NivelEducativoId",
          ),
          perfilesLaft: c?.perfiles_laft || [],
          padecimientos: c?.padecimientos || [],
          profesiones: c?.profesiones || [],
          actividadesEconomicasCnbs: c?.actividades_economicas_cnbs || [],
          profesionesCnbs: c?.profesiones_cnbs || [],
          viviendaTipos: c?.vivienda_tipos || [],
          ingresosAproximados: c?.ingresos_aproximados || [],
          girosEntidad: c?.giros_entidad || [],
          cargos: sortCatalogById(c?.cargos || [], "CargoId"),
          departamentos: c?.departamentos || [],
          municipios: c?.municipios || [],
          colonias: c?.colonias || [],
          parentescos: c?.parentescos || [],
          tipoClientes: c?.tipo_cliente_solicitantes || [],
          referenciaTipos: c?.referencia_tipos || [],
        });
      } catch (_err) {
        if (!active) return;
        setCatalogos({ ...emptyCatalogos });
      } finally {
        if (active) setLoadingCatalogos(false);
      }
    };

    loadCatalogos();
    return () => {
      active = false;
    };
  }, [authToken]);

  useEffect(() => {
    if (String(form?.tipoCliente ?? "").trim()) return;
    const firstTipoClienteId = String(
      catalogos?.tipoClientes?.[0]?.TipoClienteSolicitanteId ?? "",
    ).trim();
    if (!firstTipoClienteId) return;

    setForm((prev) => ({ ...prev, tipoCliente: firstTipoClienteId }));
    setErrors((prev) => {
      if (!prev?.tipoCliente) return prev;
      const next = { ...prev };
      delete next.tipoCliente;
      return next;
    });
  }, [catalogos.tipoClientes, form.tipoCliente]);

  const municipiosResidencia = useMemo(
    () =>
      (catalogos.municipios || []).filter(
        (m) =>
          !form.departamentoResidencia ||
          String(m?.FKDepartamentoId || "").trim() ===
            String(form.departamentoResidencia || "").trim(),
      ),
    [catalogos.municipios, form.departamentoResidencia],
  );

  const coloniasResidencia = useMemo(
    () =>
      (catalogos.colonias || []).filter(
        (c) =>
          !form.municipioResidencia ||
          String(c?.FKMunicipioId || "").trim() ===
            String(form.municipioResidencia || "").trim(),
      ),
    [catalogos.colonias, form.municipioResidencia],
  );
  const normalizeColoniaResidenciaText = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .trim();
  const coloniaResidenciaDisplayValue = useMemo(() => {
    const currentValue = String(form.coloniaResidencia || "").trim();
    if (!currentValue) return null;
    const normalizedCurrent = normalizeColoniaResidenciaText(currentValue);
    const matched = (catalogos.colonias || []).find((item) => {
      const optionId = String(item?.ColoniaId || "").trim();
      const optionName = String(item?.Nombre || "").trim();
      return (
        optionId === currentValue ||
        normalizeColoniaResidenciaText(optionName) === normalizedCurrent
      );
    });
    return String(matched?.Nombre ?? currentValue).trim() || null;
  }, [catalogos.colonias, form.coloniaResidencia]);
  const coloniaResidenciaOptions = useMemo(() => {
    const unique = new Map();
    (coloniasResidencia || []).forEach((item) => {
      const label = String(item?.Nombre || "").trim();
      const key = normalizeColoniaResidenciaText(label);
      if (label && key && !unique.has(key)) unique.set(key, label);
    });
    const currentValue = String(coloniaResidenciaDisplayValue || "").trim();
    const currentKey = normalizeColoniaResidenciaText(currentValue);
    if (currentValue && currentKey && !unique.has(currentKey)) {
      unique.set(currentKey, currentValue);
    }
    return Array.from(unique.values());
  }, [coloniasResidencia, coloniaResidenciaDisplayValue]);
  const handleColoniaResidenciaChange = (selectedValue) => {
    setFieldValue("coloniaResidencia", String(selectedValue ?? "").trim());
  };
  useEffect(() => {
    const currentValue = String(form.coloniaResidencia || "").trim();
    if (!currentValue) return;
    const matched = (catalogos.colonias || []).find(
      (item) => String(item?.ColoniaId || "").trim() === currentValue,
    );
    const nextValue = String(matched?.Nombre || "").trim();
    if (!nextValue || nextValue === currentValue) return;
    setForm((prev) => {
      if (String(prev.coloniaResidencia || "").trim() !== currentValue) {
        return prev;
      }
      return { ...prev, coloniaResidencia: nextValue };
    });
  }, [catalogos.colonias, form.coloniaResidencia]);
  const profesionesSearch = useMemo(() => {
    const currentId = String(form.profesion || "").trim();
    const base = [...(catalogos.profesiones || [])];
    if (
      currentId &&
      !base.some(
        (item) => String(item?.ProfesionOficioId || "").trim() === currentId,
      )
    ) {
      base.unshift({
        ProfesionOficioId: currentId,
        Descripcion: currentId,
      });
    }
    return base;
  }, [catalogos.profesiones, form.profesion]);
  const profesionesCnbsSearch = useMemo(() => {
    const currentId = String(form.profesionCnbs || "").trim();
    const base = [...(catalogos.profesionesCnbs || [])];
    if (
      currentId &&
      !base.some((item) => String(item?.ProfesionCnbsID || "").trim() === currentId)
    ) {
      base.unshift({
        ProfesionCnbsID: currentId,
        NombreProfesionCnbs: currentId,
      });
    }
    return base;
  }, [catalogos.profesionesCnbs, form.profesionCnbs]);
  const cargosSearch = useMemo(() => {
    const currentId = String(form.cargoDesempenado || "").trim();
    const base = sortCatalogById(catalogos.cargos || [], "CargoId");
    if (
      currentId &&
      !base.some((item) => String(item?.CargoId || "").trim() === currentId)
    ) {
      base.unshift({
        CargoId: currentId,
        Descripcion: currentId,
      });
    }
    return base;
  }, [catalogos.cargos, form.cargoDesempenado]);
  const normalizeActividadEconomicaFilter = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Z0-9]+/gi, " ")
      .toUpperCase()
      .replace(/\s+/g, " ")
      .trim();
  const getActividadEconomicaCatalogLabel = (option) =>
    String(
      option?.NombreActividadEconomica ??
        option?.ActividadEconomicaCnbsIDdesc ??
        option?.ActividadEconomicaCnbsID ??
        "",
    )
      .trim()
      .toUpperCase();
  const actividadEconomicaCnbsOptions = useMemo(() => {
    const unique = new Map();
    (catalogos.actividadesEconomicasCnbs || []).forEach((item) => {
      const label = getActividadEconomicaCatalogLabel(item);
      if (!label) return;
      if (!unique.has(label)) unique.set(label, label);
    });
    return Array.from(unique.values());
  }, [catalogos.actividadesEconomicasCnbs]);
  const actividadEconomicaOptionMatchesCurrent = (option, currentRaw) => {
    const current = normalizeActividadEconomicaFilter(currentRaw);
    if (!current) return false;
    const id = normalizeActividadEconomicaFilter(
      String(option?.ActividadEconomicaCnbsID ?? ""),
    );
    const label = normalizeActividadEconomicaFilter(
      String(option?.NombreActividadEconomica ?? ""),
    );
    const idDesc = normalizeActividadEconomicaFilter(
      String(option?.ActividadEconomicaCnbsIDdesc ?? ""),
    );
    return current === id || current === label || current === idDesc;
  };
  const actividadEconomicaCnbsCurrentLabel = useMemo(() => {
    const currentRaw = String(form.actividadEconomicaCnbs ?? "").trim();
    if (!currentRaw) return "";
    const matched = (catalogos.actividadesEconomicasCnbs || []).find((option) =>
      actividadEconomicaOptionMatchesCurrent(option, currentRaw),
    );
    return matched
      ? getActividadEconomicaCatalogLabel(matched)
      : currentRaw.toUpperCase();
  }, [catalogos.actividadesEconomicasCnbs, form.actividadEconomicaCnbs]);
  const actividadEconomicaCnbsSearchOptions = useMemo(() => {
    const current = String(actividadEconomicaCnbsCurrentLabel || "").trim();
    const base = [...actividadEconomicaCnbsOptions];
    if (
      current &&
      !base.some(
        (item) =>
          normalizeActividadEconomicaFilter(item) ===
          normalizeActividadEconomicaFilter(current),
      )
    ) {
      base.unshift(current);
    }
    return base;
  }, [actividadEconomicaCnbsOptions, actividadEconomicaCnbsCurrentLabel]);
  const filterActividadEconomicaCnbsOptions = (options, state) => {
    const query = normalizeActividadEconomicaFilter(state?.inputValue);
    if (!query) return options;
    return (options || []).filter((option) =>
      normalizeActividadEconomicaFilter(option).includes(query),
    );
  };
  const filterCargoOptions = (options, state) => {
    const query = normalizeActividadEconomicaFilter(state?.inputValue);
    if (!query) return options;
    return (options || []).filter((option) =>
      normalizeActividadEconomicaFilter(
        `${option?.CargoId ?? ""} ${option?.Descripcion ?? ""}`,
      ).includes(query),
    );
  };
  const handleActividadEconomicaCnbsSelect = (selectedValue) => {
    const selectedLabel = String(selectedValue ?? "").trim();
    if (!selectedLabel) {
      setFieldValue("actividadEconomicaCnbs", "");
      return;
    }
    const matched = (catalogos.actividadesEconomicasCnbs || []).find((item) => {
      const label = getActividadEconomicaCatalogLabel(item);
      return (
        normalizeActividadEconomicaFilter(label) ===
        normalizeActividadEconomicaFilter(selectedLabel)
      );
    });
    setFieldValue(
      "actividadEconomicaCnbs",
      String(matched?.ActividadEconomicaCnbsID ?? selectedLabel).trim(),
    );
  };
  const buildAntiguedadLaboral = (value) => {
    const iso = toIsoDate(value);
    if (!iso) return "";
    const ingresoDate = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(ingresoDate.getTime())) return "";

    const today = new Date();
    if (ingresoDate > today) return "0 AÑOS, 0 MESES, 0 DÍAS";

    let years = today.getFullYear() - ingresoDate.getFullYear();
    let months = today.getMonth() - ingresoDate.getMonth();
    let days = today.getDate() - ingresoDate.getDate();

    if (days < 0) {
      months -= 1;
      const prevMonthLastDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        0,
      ).getDate();
      days += prevMonthLastDay;
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return `${Math.max(0, years)} AÑOS, ${Math.max(0, months)} MESES, ${Math.max(0, days)} DÍAS`;
  };
  const parseIsoDate = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return null;
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const parsed = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    );
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  };
  const calculateYearsElapsed = (fromDate, toDate = new Date()) => {
    if (!(fromDate instanceof Date) || Number.isNaN(fromDate.getTime()))
      return "";
    if (!(toDate instanceof Date) || Number.isNaN(toDate.getTime())) return "";
    if (fromDate > toDate) return "0";

    let years = toDate.getFullYear() - fromDate.getFullYear();
    const monthDiff = toDate.getMonth() - fromDate.getMonth();
    const dayDiff = toDate.getDate() - fromDate.getDate();
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      years -= 1;
    }
    return String(Math.max(0, years));
  };
  const clampTiempoResidirValue = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return "";
    return String(Math.min(MAX_TIEMPO_RESIDIR, Math.max(0, parsed)));
  };
  const buildFechaDesdeResideFromYears = (yearsRaw) => {
    const parsedYears = Number.parseInt(clampTiempoResidirValue(yearsRaw), 10);
    if (!Number.isFinite(parsedYears) || parsedYears < 0) return "";
    const today = new Date();
    const target = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    target.setFullYear(target.getFullYear() - parsedYears);
    const yyyy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, "0");
    const dd = String(target.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const municipiosTrabajo = useMemo(
    () =>
      (catalogos.municipios || []).filter(
        (m) =>
          !form.departamentoTrabajo ||
          String(m?.FKDepartamentoId || "").trim() ===
            String(form.departamentoTrabajo || "").trim(),
      ),
    [catalogos.municipios, form.departamentoTrabajo],
  );

  const coloniasTrabajo = useMemo(
    () =>
      (catalogos.colonias || []).filter(
        (c) =>
          !form.municipioTrabajo ||
          String(c?.FKMunicipioId || "").trim() ===
            String(form.municipioTrabajo || "").trim(),
      ),
    [catalogos.colonias, form.municipioTrabajo],
  );
  const coloniaTrabajoDisplayValue = useMemo(() => {
    const currentValue = String(form.coloniaTrabajo || "").trim();
    if (!currentValue) return null;
    const normalizedCurrent = normalizeColoniaResidenciaText(currentValue);
    const matched = (catalogos.colonias || []).find((item) => {
      const optionId = String(item?.ColoniaId || "").trim();
      const optionName = String(item?.Nombre || "").trim();
      return (
        optionId === currentValue ||
        normalizeColoniaResidenciaText(optionName) === normalizedCurrent
      );
    });
    return String(matched?.Nombre ?? currentValue).trim() || null;
  }, [catalogos.colonias, form.coloniaTrabajo]);
  const coloniaTrabajoOptions = useMemo(() => {
    const unique = new Map();
    (coloniasTrabajo || []).forEach((item) => {
      const label = String(item?.Nombre || "").trim();
      const key = normalizeColoniaResidenciaText(label);
      if (label && key && !unique.has(key)) unique.set(key, label);
    });
    const currentValue = String(coloniaTrabajoDisplayValue || "").trim();
    const currentKey = normalizeColoniaResidenciaText(currentValue);
    if (currentValue && currentKey && !unique.has(currentKey)) {
      unique.set(currentKey, currentValue);
    }
    return Array.from(unique.values());
  }, [coloniasTrabajo, coloniaTrabajoDisplayValue]);
  const handleColoniaTrabajoChange = (selectedValue) => {
    setFieldValue("coloniaTrabajo", String(selectedValue ?? "").trim());
  };
  useEffect(() => {
    const currentValue = String(form.coloniaTrabajo || "").trim();
    if (!currentValue) return;
    const matched = (catalogos.colonias || []).find(
      (item) => String(item?.ColoniaId || "").trim() === currentValue,
    );
    const nextValue = String(matched?.Nombre || "").trim();
    if (!nextValue || nextValue === currentValue) return;
    setForm((prev) => {
      if (String(prev.coloniaTrabajo || "").trim() !== currentValue) {
        return prev;
      }
      return { ...prev, coloniaTrabajo: nextValue };
    });
  }, [catalogos.colonias, form.coloniaTrabajo]);

  const setField = (field) => (event) => {
    const raw =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    let value =
      typeof raw === "string"
        ? raw.replace(/[\r\n]+/g, " ").replace(/  +/g, " ")
        : raw;
    if (typeof value === "string") {
      if (_NAME_FIELDS.includes(field)) {
        value = value.replace(_ONLY_LETTERS, "").toUpperCase();
      } else if (_NUM_FIELDS.includes(field)) {
        value = value.replace(_ONLY_DIGITS, "");
      } else if (_UPPER_FIELDS.includes(field)) {
        value = value.toUpperCase();
      } else if (field === "correo") {
        value = value.replace(/[ñÑ]/g, "");
      }
    }
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "fechaIngresoInstitucion") {
        next.antiguedadLaboral = buildAntiguedadLaboral(value);
      }
      if (field === "poseeVehiculo" && !value) next.numeroVehiculos = "";
      if (field === "poseeInmuebles" && !value) next.descOtrosInmuebles = "";
      if (field === "otrosIngresos" && !value) {
        next.descOtrosIngresos = "";
        next.montoOtrosIngresos = "";
      }
      return next;
    });
    setErrors((prev) => {
      if (field === "correo" && typeof value === "string" && value.trim()) {
        if (!_EMAIL_REGEX.test(value.trim())) {
          return { ...prev, correo: "Formato de correo inválido" };
        }
      }
      if (!prev[field] && !(field === "fechaIngresoInstitucion" && prev.antiguedadLaboral)) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      if (field === "fechaIngresoInstitucion") delete next.antiguedadLaboral;
      return next;
    });
    setMessage("");
    setOk("");
  };
  const setFieldValue = (field, value) => {
    setField(field)({
      target: {
        type: "text",
        value: value ?? "",
      },
    });
  };
  const normalizeCorreoConArroba = (value) => {
    const cleaned = String(value ?? "")
      .trim()
      .replace(/\s+/g, "")
      .replace(/[ñÑ]/g, "");
    if (!cleaned) return "";
    if (cleaned.includes("@")) return cleaned;

    const lower = cleaned.toLowerCase();
    const knownDomains = [
      "gmail.com",
      "hotmail.com",
      "outlook.com",
      "yahoo.com",
      "icloud.com",
      "injupemp.gob.hn",
      "injupemp.hn",
    ];
    for (const domain of knownDomains) {
      if (!lower.endsWith(domain)) continue;
      const localPart = cleaned
        .slice(0, cleaned.length - domain.length)
        .replace(/[@._-]+$/, "");
      if (localPart) return `${localPart}@${domain}`;
    }
    return `${cleaned}@`;
  };
  const handleCorreoBlur = () => {
    const normalized = normalizeCorreoConArroba(form.correo);
    if (normalized !== form.correo) {
      setForm((prev) => ({ ...prev, correo: normalized }));
    }
    setErrors((prev) => {
      if (!normalized) {
        if (!prev.correo) return prev;
        const next = { ...prev };
        delete next.correo;
        return next;
      }
      if (!_EMAIL_REGEX.test(normalized.trim())) {
        return { ...prev, correo: "Formato de correo inválido" };
      }
      if (!prev.correo) return prev;
      const next = { ...prev };
      delete next.correo;
      return next;
    });
  };
  const handleTiempoResidirChange = (event) => {
    const value = clampTiempoResidirValue(event?.target?.value ?? "");
    setForm((prev) => ({
      ...prev,
      tiempoResidir: value,
      fechaDesdeReside:
        String(value).trim() === ""
          ? ""
          : buildFechaDesdeResideFromYears(value) || prev.fechaDesdeReside,
    }));
    setErrors((prev) => {
      if (!prev.tiempoResidir && !prev.fechaDesdeReside) return prev;
      const next = { ...prev };
      delete next.tiempoResidir;
      delete next.fechaDesdeReside;
      return next;
    });
    setMessage("");
    setOk("");
  };
  const handleFechaDesdeResideChange = (event) => {
    const value = event?.target?.value ?? "";
    const parsedDate = parseIsoDate(value);
    setForm((prev) => ({
      ...prev,
      fechaDesdeReside: value,
      tiempoResidir:
        value === ""
          ? ""
          : parsedDate
            ? clampTiempoResidirValue(calculateYearsElapsed(parsedDate))
            : prev.tiempoResidir,
    }));
    setErrors((prev) => {
      if (!prev.fechaDesdeReside && !prev.tiempoResidir) return prev;
      const next = { ...prev };
      delete next.fechaDesdeReside;
      delete next.tiempoResidir;
      return next;
    });
    setMessage("");
    setOk("");
  };

  const isBlank = (v) => `${v ?? ""}`.trim() === "";
  const buildMissingRequiredFields = (validationErrors = {}) => {
    const labels = [];
    const added = new Set();
    Object.keys(validationErrors || {}).forEach((field) => {
      const label = REQUIRED_FIELD_LABELS[field] || field;
      if (added.has(label)) return;
      added.add(label);
      labels.push(label);
    });
    return labels;
  };

  const validate = () => {
    const next = {};
    topRequired.forEach((f) => {
      if (isBlank(form[f])) next[f] = "Campo obligatorio";
    });
    if (isBlank(form.departamentoResidencia))
      next.departamentoResidencia = "Campo obligatorio";
    if (isBlank(form.municipioResidencia))
      next.municipioResidencia = "Campo obligatorio";
    if (isBlank(form.coloniaResidencia))
      next.coloniaResidencia = "Campo obligatorio";
    if (isBlank(form.direccionCompleta))
      next.direccionCompleta = "Campo obligatorio";
    if (isBlank(form.tiempoResidir)) next.tiempoResidir = "Campo obligatorio";
    if (isBlank(form.fechaDesdeReside))
      next.fechaDesdeReside = "Campo obligatorio";
    if (isBlank(form.movil)) next.movil = "Campo obligatorio";
    laboralRequired.forEach((f) => {
      if (isBlank(form[f])) next[f] = "Campo obligatorio";
    });
    if (isBlank(form.tipoVivienda)) next.tipoVivienda = "Campo obligatorio";
    if (form.poseeVehiculo && isBlank(form.numeroVehiculos))
      next.numeroVehiculos = "Campo obligatorio";
    if (form.poseeInmuebles && isBlank(form.descOtrosInmuebles))
      next.descOtrosInmuebles = "Campo obligatorio";
    if (form.otrosIngresos) {
      if (isBlank(form.descOtrosIngresos))
        next.descOtrosIngresos = "Campo obligatorio";
      if (isBlank(form.montoOtrosIngresos))
        next.montoOtrosIngresos = "Campo obligatorio";
    }
    if (!form.perfilLaft) next.perfilLaft = "Campo obligatorio";
    if (!isBlank(form.correo) && !_EMAIL_REGEX.test(form.correo.trim())) {
      next.correo = "Formato de correo inválido";
    }
    setErrors(next);
    return next;
  };

  const normalizeReferenciaTipo = (value) => {
    const text = `${value ?? ""}`.trim().toUpperCase();
    if (!text) return "";
    if (text.includes("COMERCIAL") || text.includes("BANC"))
      return "COMERCIALES / BANCARIAS";
    if (text.includes("LABOR")) return "LABORALES";
    if (text.includes("PERSON")) return "PERSONALES";
    return text;
  };

  const getReferenciaTipoNombre = (tipoId, tipoNombreFallback = "") => {
    const selectedId = String(tipoId || "").trim();
    if (selectedId) {
      const found = (catalogos.referenciaTipos || []).find(
        (item) => String(item?.ReferenciaId || "").trim() === selectedId,
      );
      const fromCatalog = String(found?.Nombre || "").trim();
      if (fromCatalog) return fromCatalog;
    }
    return String(tipoNombreFallback || "").trim();
  };

  const getPadecimientoDescripcion = (padId, fallback = "") => {
    const id = String(padId || "").trim();
    if (id) {
      const found = (catalogos.padecimientos || []).find(
        (item) => String(item?.PadecimientoMedicoId || "").trim() === id,
      );
      const desc = String(found?.Descripcion || "").trim();
      if (desc) return desc;
    }
    return String(fallback || "").trim();
  };

  const referenciaDireccionAplica = (refForm = referenciaForm) => {
    const tipoNombre = getReferenciaTipoNombre(
      refForm?.FKReferenciaTipoId,
      refForm?.tipoReferencia,
    );
    const tipoNormalizado = normalizeReferenciaTipo(tipoNombre);
    return tipoNormalizado === "PERSONALES" || tipoNormalizado === "LABORALES";
  };

  const getReferenceCountByType = (tipo) =>
    (referencias || []).filter((item) => {
      const tipoNombre = getReferenciaTipoNombre(
        item?.FKReferenciaTipoId,
        item?.tipoReferencia,
      );
      return normalizeReferenciaTipo(tipoNombre) === normalizeReferenciaTipo(tipo);
    }).length;

  const getReferenciasRequirementError = () => {
    const faltantes = [];
    if (getReferenceCountByType("PERSONALES") < 2) {
      faltantes.push("2 Personales");
    }
    if (getReferenceCountByType("COMERCIALES / BANCARIAS") < 1) {
      faltantes.push("1 Comercial/Bancaria");
    }
    return faltantes.length
      ? `Faltan referencias obligatorias: ${faltantes.join(", ")}.`
      : "";
  };

  const getReferenciasRequirementSummary = () =>
    "Requerido: 2 Personales y 1 Comercial/Bancaria.";

  const getBeneficiariosRequirementError = () => {
    const totalPotenciales = (beneficiarios || []).length;
    if (totalPotenciales < 1)
      return "Debe ingresar al menos 1 beneficiario potencial.";
    return "";
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setOk("");
      setMissingRequiredFields(buildMissingRequiredFields(e));
      setMessage("Revise los campos obligatorios.");
      return;
    }
    setMissingRequiredFields([]);
    const referenciasError = getReferenciasRequirementError();
    if (referenciasError) {
      setOk("");
      setMissingRequiredFields([]);
      setMessage(referenciasError);
      return;
    }
    const beneficiariosError = getBeneficiariosRequirementError();
    if (beneficiariosError) {
      setOk("");
      setMissingRequiredFields([]);
      setMessage(beneficiariosError);
      return;
    }
    try {
      setSaving(true);
      const payload = buildActualizarPayload({
        form,
        referencias,
        beneficiarios,
        padecimientos,
        cargosPublicos: [],
        empresasPpe,
        familiaresPpe,
      });
      payload.persona.userIdActualizo = sessi?.sessionData?.id ?? null;
      await apiRequest("/Enrolamiento/personas/guardar/", {
        authToken,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setMissingRequiredFields([]);
      setMessage("");
      setOk("Datos de afiliación actualizados correctamente.");
    } catch (err) {
      setMissingRequiredFields([]);
      setOk("");
      setMessage(err?.message || "No se pudo actualizar la información.");
    } finally {
      setSaving(false);
    }
  };

  const input = (name, label, extra = {}) => (
    <TextField
      size="small"
      label={label}
      value={
        IDENTIFICACION_FIELDS.has(name)
          ? formatIdentificacionVisual(form[name])
          : TELEFONO_FIELDS.has(name)
            ? formatTelefonoVisual(form[name])
            : form[name]
      }
      onChange={setField(name)}
      error={Boolean(errors[name])}
      helperText={errors[name] || ""}
      {...extra}
    />
  );

  const selectInput = ({
    name,
    label,
    options = [],
    valueKey,
    labelKey,
    onChange,
    disabled = false,
  }) => (
    <FormControl
      size="small"
      fullWidth
      error={Boolean(errors[name])}
      disabled={disabled}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={form[name] ?? ""}
        onChange={onChange || setField(name)}
      >
        <MenuItem value="">
          <em>Seleccione</em>
        </MenuItem>
        {options.map((item, idx) => {
          const value = String(item?.[valueKey] ?? "").trim();
          const text = String(item?.[labelKey] ?? value).trim();
          return (
            <MenuItem key={`${name}-${value || idx}`} value={value}>
              {text}
            </MenuItem>
          );
        })}
      </Select>
      {errors[name] ? (
        <Typography variant="caption" sx={{ color: "#d32f2f", ml: 1 }}>
          {errors[name]}
        </Typography>
      ) : null}
    </FormControl>
  );

  const handleDepartamentoResidenciaChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      departamentoResidencia: value,
      municipioResidencia: "",
      coloniaResidencia: "",
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.departamentoResidencia;
      delete next.municipioResidencia;
      delete next.coloniaResidencia;
      return next;
    });
    setMessage("");
    setOk("");
  };

  const handleMunicipioResidenciaChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      municipioResidencia: value,
      coloniaResidencia: "",
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.municipioResidencia;
      delete next.coloniaResidencia;
      return next;
    });
    setMessage("");
    setOk("");
  };

  const handleDepartamentoTrabajoChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      departamentoTrabajo: value,
      municipioTrabajo: "",
      coloniaTrabajo: "",
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.departamentoTrabajo;
      delete next.municipioTrabajo;
      delete next.coloniaTrabajo;
      return next;
    });
    setMessage("");
    setOk("");
  };

  const handleMunicipioTrabajoChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      municipioTrabajo: value,
      coloniaTrabajo: "",
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.municipioTrabajo;
      delete next.coloniaTrabajo;
      return next;
    });
    setMessage("");
    setOk("");
  };

  const formatSearchDate = (value) => {
    const iso = toIsoDate(value);
    if (!iso) return "";
    const [year, month, day] = iso.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatDisplayDate = (value) => formatSearchDate(value);

  const calculateDaysTranscurridos = (value) => {
    const iso = toIsoDate(value);
    if (!iso) return "";
    const fromDate = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(fromDate.getTime())) return "";
    const now = new Date();
    const ms = now.getTime() - fromDate.getTime();
    if (ms < 0) return "0";
    return String(Math.floor(ms / (1000 * 60 * 60 * 24)));
  };

  const getCatalogLabel = (options, valueKey, labelKey, value) => {
    const id = String(value || "").trim();
    if (!id) return "";
    const match = (options || []).find(
      (item) => String(item?.[valueKey] || "").trim() === id,
    );
    return match ? String(match?.[labelKey] || id) : id;
  };

  const sanitizeAdjuntoPath = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) {
      return raw.replace("/api/api/", "/api/");
    }
    return raw.replace(/^\/api\/api\//i, "/api/");
  };

  const resolveAdjuntoUrl = (path) => {
    const sanitized = sanitizeAdjuntoPath(path);
    if (!sanitized) return "";
    if (/^https?:\/\//i.test(sanitized)) return sanitized;

    const withoutApiPrefix = sanitized.startsWith("/api/")
      ? sanitized.slice(4)
      : sanitized;
    return buildApiUrl(withoutApiPrefix);
  };

  const mapAdjuntos = (rows = []) =>
    (rows || []).map((item, index) => {
      const nombre = item?.Nombre || item?.ArchivoNombre || "";
      const tipo =
        item?.Tipo ||
        (nombre.includes(".") ? `.${nombre.split(".").pop()}` : "");
      const downloadPath =
        item?.download_url ||
        (item?.AdjuntoId
          ? `/Enrolamiento/adjuntos/${item.AdjuntoId}/descargar/`
          : "");
      return {
        id: item?.AdjuntoId || item?.FotoId || `adj-${index}`,
        nombre,
        descripcion: item?.Descripcion || item?.Observaciones || nombre,
        entidad: String(item?.Entidad || item?.entidad || "").trim(),
        tipo,
        tamanio: item?.Tamanio || item?.tamanio_bytes || "",
        fechaCrea: item?.FechaCrea || "",
        downloadUrl: resolveAdjuntoUrl(downloadPath),
      };
    });

  const closeAdjuntoPreview = () => {
    setOpenAdjuntoPreview(false);
    setPreviewAdjuntoError("");
    setPreviewAdjuntoLoading(false);
    if (previewAdjuntoUrl) {
      URL.revokeObjectURL(previewAdjuntoUrl);
    }
    setPreviewAdjuntoUrl("");
    setPreviewAdjuntoMime("");
  };

  const handleOpenAdjuntoPreview = async (item) => {
    const url = String(item?.downloadUrl || "").trim();
    if (!url) return;

    if (previewAdjuntoUrl) {
      URL.revokeObjectURL(previewAdjuntoUrl);
      setPreviewAdjuntoUrl("");
    }
    setOpenAdjuntoPreview(true);
    setPreviewAdjuntoLoading(true);
    setPreviewAdjuntoError("");
    setPreviewAdjuntoMime("");

    try {
      const token = getAccessToken(authToken);
      const headers = token ? { Authorization: token } : {};
      const response = await fetch(url, { method: "GET", headers });
      if (!response.ok) {
        throw new Error(
          `No se pudo abrir el adjunto (HTTP ${response.status}).`,
        );
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewAdjuntoUrl(blobUrl);
      setPreviewAdjuntoMime(blob?.type || "application/octet-stream");
    } catch (err) {
      setPreviewAdjuntoError(err?.message || "No se pudo abrir el adjunto.");
    } finally {
      setPreviewAdjuntoLoading(false);
    }
  };

  const fetchAdjuntosPersona = async (personaId) => {
    const id = `${personaId ?? ""}`.trim();
    if (!id || !authToken) return;
    try {
      setCargandoAdjuntos(true);
      const data = await apiRequest(
        `/Enrolamiento/personas/${encodeURIComponent(id)}/adjuntos/?entidad=afiliacion`,
        { authToken },
      );
      setAdjuntos(mapAdjuntos(data?.adjuntos || []));
    } catch (_err) {
      setAdjuntos([]);
    } finally {
      setCargandoAdjuntos(false);
    }
  };

  const hydratePersonaData = (data) => {
    const mapped = mapPersonaPayloadToForm(data, { ...emptyForm });
    const antiguedadLaboral = buildAntiguedadLaboral(
      mapped?.fechaIngresoInstitucion,
    );
    setForm({
      ...mapped,
      antiguedadLaboral,
      perfilLaft: String(mapped?.perfilLaft || DEFAULT_PERFIL_LAFT).trim() || DEFAULT_PERFIL_LAFT,
    });
    setFotoPersonaUrl(getUltimaFotoPersona(data));

    const fechaActualizacion = toIsoDate(
      data?.informacion_adicional?.FechaActualizacionInformacion ||
      data?.persona?.FechaRegistro ||
      "",
    );
    setFechaActualizacionInfo(formatDisplayDate(fechaActualizacion));
    setDiasTranscurridosInfo(calculateDaysTranscurridos(fechaActualizacion));

    const beneficiariosPotenciales = Array.isArray(data?.beneficiarios)
      ? data.beneficiarios
      : [];

    setBeneficiarios(
      beneficiariosPotenciales.map((item, index) => ({
        id:
          item?.BeneficiarioId ||
          `benef-${index}-${item?.DocumentoNumero || ""}`,
        beneficiarioId: item?.BeneficiarioId || null,
        dni: item?.DocumentoNumero || "",
        primerNombre: item?.Nombre1 || "",
        segundoNombre: item?.Nombre2 || "",
        primerApellido: item?.Apellido1 || "",
        segundoApellido: item?.Apellido2 || "",
        parentesco: String(item?.FKParentescoId || "").trim(),
        tipoDocumento: String(item?.FKDocumentoTipoId || "").trim(),
        porcentaje:
          item?.Porcentaje !== undefined && item?.Porcentaje !== null
            ? String(item?.Porcentaje)
            : "0",
        FKDesignacionId: item?.FKDesignacionId || null,
      })),
    );

    setReferencias(
      (data?.referencias || []).map((item, index) => ({
        id: item?.ReferenciaId || `ref-${index}-${item?.Nombre || ""}`,
        referenciaId: item?.ReferenciaId || null,
        nombreCompleto: item?.Nombre || "",
        telefono: item?.Telefono || "",
        direccion: item?.Direccion || "",
        FKReferenciaTipoId: String(item?.FKReferenciaTipoId || "").trim(),
        tipoReferencia:
          getCatalogLabel(
            catalogos.referenciaTipos || [],
            "ReferenciaId",
            "Nombre",
            item?.FKReferenciaTipoId,
          ) || "",
      })),
    );

    setPadecimientos(
      (data?.padecimientos || []).map((item, index) => {
        const padId = item?.FKPadecimientoMedicoId || item?.id || "";
        return {
          id: padId || `pad-${index}`,
          descripcion:
            getCatalogLabel(
              catalogos.padecimientos || [],
              "PadecimientoMedicoId",
              "Descripcion",
              padId,
            ) ||
            item?.Descripcion ||
            item?.nombre ||
            "",
        };
      }),
    );

    setEmpresasPpe(
      (data?.relacion_empresas || []).map((item) => ({
        id: item?.RelacionEmpresaId || Date.now(),
        razonSocial: item?.RazonSocial || "",
        rtn: item?.RTN || "",
        pais: String(item?.FKNacionalidadId || "").trim(),
        giroEntidad: String(item?.FKGiroEntidadId || "").trim(),
      })),
    );
    const familiaresRaw =
      data?.persona_politicamente_expuesta_familiares ||
      data?.personaPoliticamenteExpuestaFamiliares ||
      data?.familiares_ppe ||
      data?.ppe_familiares ||
      [];
    setFamiliaresPpe(
      (Array.isArray(familiaresRaw) ? familiaresRaw : []).map(
        (item, index) => ({
          id:
            item?.PersonaPolicamenteExpuestaFamiliaresId ||
            item?.personaPolicamenteExpuestaFamiliaresId ||
            `fam-${index}-${item?.Nombre1 || item?.primerNombre || ""}`,
          personaPolicamenteExpuestaFamiliaresId:
            item?.PersonaPolicamenteExpuestaFamiliaresId ||
            item?.personaPolicamenteExpuestaFamiliaresId ||
            null,
          primerNombre: item?.Nombre1 || item?.primerNombre || "",
          segundoNombre: item?.Nombre2 || item?.segundoNombre || "",
          primerApellido: item?.Apellido1 || item?.primerApellido || "",
          segundoApellido: item?.Apellido2 || item?.segundoApellido || "",
          parentesco: String(
            item?.FKParentescoId ?? item?.parentesco ?? "",
          ).trim(),
          nacionalidad: String(
            item?.FKNacionalidadId ?? item?.nacionalidad ?? "",
          ).trim(),
        }),
      ),
    );
    setAdjuntos(mapAdjuntos(data?.adjuntos || []));
    setAdjuntoFile(null);
    setAdjuntoDescripcion("");
    if (adjuntoInputRef.current) {
      adjuntoInputRef.current.value = "";
    }

    setErrors({});
    setSearchError("");
    setSearchResults([]);
    setMostrarFormulario(true);
    setMessage("");
    setOk("");
  };

  const fetchPersonaById = async (personaId) => {
    const id = `${personaId ?? ""}`.trim();
    if (!id) return;
    const data = await apiRequest(
      `/Enrolamiento/personas/${encodeURIComponent(id)}/?beneficiario_tipo=P`,
      { authToken },
    );
    hydratePersonaData(data);
    await fetchAdjuntosPersona(id);
  };

  const handleBuscar = async () => {
    const cleanedDni = (dniBusqueda || "").trim();
    const cleanedNombre = (primerNombreBusqueda || "").trim();
    const cleanedApellido = (primerApellidoBusqueda || "").trim();
    const hasDni = Boolean(cleanedDni);
    const hasNombreData = Boolean(cleanedNombre || cleanedApellido);

    if (hasDni && hasNombreData) {
      setSearchError(
        "Busque por DNI o por Primer Nombre + Primer Apellido, no ambos.",
      );
      setSearchResults([]);
      setMostrarFormulario(false);
      return;
    }

    if (!hasDni && !(cleanedNombre && cleanedApellido)) {
      setSearchError("Ingrese DNI o complete Primer Nombre y Primer Apellido.");
      setSearchResults([]);
      setMostrarFormulario(false);
      return;
    }

    try {
      setSearching(true);
      const query = hasDni
        ? `dni=${encodeURIComponent(cleanedDni)}&beneficiario_tipo=P`
        : `primer_nombre=${encodeURIComponent(cleanedNombre)}&primer_apellido=${encodeURIComponent(cleanedApellido)}&beneficiario_tipo=P`;
      const data = await apiRequest(`/Enrolamiento/personas/buscar/?${query}`, {
        authToken,
      });

      if (data?.persona) {
        hydratePersonaData(data);
        await fetchAdjuntosPersona(
          data?.persona?.PersonaId || data?.persona?.personaId || "",
        );
        return;
      }

      const rows = Array.isArray(data?.resultados) ? data.resultados : [];
      setSearchResults(rows);
      setSearchError(
        rows.length
          ? ""
          : "No se encontraron registros con los criterios ingresados.",
      );
      setMostrarFormulario(false);
      setMessage("");
      setOk("");
    } catch (err) {
      setSearchError(
        err?.message || "No se encontró registro con los criterios ingresados.",
      );
      setSearchResults([]);
      setMostrarFormulario(false);
    } finally {
      setSearching(false);
    }
  };

  const handleSeleccionarResultado = async (row) => {
    const personaId = row?.PersonaId || row?.personaId || "";
    if (!personaId) {
      setSearchError("No se pudo identificar el registro seleccionado.");
      return;
    }

    try {
      setSearching(true);
      await fetchPersonaById(personaId);
    } catch (err) {
      setSearchError(
        err?.message || "No se pudo cargar la información del afiliado.",
      );
      setMostrarFormulario(false);
    } finally {
      setSearching(false);
    }
  };

  const handleLimpiarBusqueda = () => {
    closeAdjuntoPreview();
    setDniBusqueda("");
    setPrimerNombreBusqueda("");
    setPrimerApellidoBusqueda("");
    setForm({ ...emptyForm });
    setFotoPersonaUrl("");
    setFechaActualizacionInfo("");
    setDiasTranscurridosInfo("");
    setBeneficiarios([]);
    setReferencias([]);
    setPadecimientos([]);
    setAdjuntos([]);
    setAdjuntoFile(null);
    setAdjuntoDescripcion("");
    if (adjuntoInputRef.current) {
      adjuntoInputRef.current.value = "";
    }
    setFamiliaresPpe([]);
    setEmpresasPpe([]);
    resetBeneficiarioForm();
    resetReferenciaForm();
    resetPadecimientoForm();
    resetFamiliarForm();
    resetEmpresaForm();
    setOpenBeneficiario(false);
    setOpenReferencia(false);
    setOpenPadecimiento(false);
    setOpenFamiliar(false);
    setOpenEmpresa(false);
    setSearchError("");
    setSearchResults([]);
    setMostrarFormulario(false);
    setMessage("");
    setOk("");
    setErrors({});
    setTab(0);
  };

  const resetBeneficiarioForm = () => {
    setBeneficiarioForm({
      beneficiarioId: null,
      dni: "",
      primerNombre: "",
      segundoNombre: "",
      primerApellido: "",
      segundoApellido: "",
      parentesco: "",
      tipoDocumento: form.tipoDocumento || "",
      porcentaje: "0",
      FKDesignacionId: null,
    });
    setBeneficiarioErrors({});
    setEditBeneficiarioId(null);
  };

  const openNuevoBeneficiario = () => {
    resetBeneficiarioForm();
    setOpenBeneficiario(true);
  };

  const openEditarBeneficiario = (item) => {
    setBeneficiarioForm({
      beneficiarioId: item?.beneficiarioId || null,
      dni: item?.dni || "",
      primerNombre: item?.primerNombre || "",
      segundoNombre: item?.segundoNombre || "",
      primerApellido: item?.primerApellido || "",
      segundoApellido: item?.segundoApellido || "",
      parentesco: item?.parentesco || "",
      tipoDocumento: item?.tipoDocumento || form.tipoDocumento || "",
      porcentaje: item?.porcentaje || "0",
      FKDesignacionId: item?.FKDesignacionId || null,
    });
    setBeneficiarioErrors({});
    setEditBeneficiarioId(item?.id);
    setOpenBeneficiario(true);
  };

  const validateBeneficiario = () => {
    const next = {};
    if (isBlank(beneficiarioForm.dni)) next.dni = "Campo obligatorio";
    if (isBlank(beneficiarioForm.primerNombre))
      next.primerNombre = "Campo obligatorio";
    if (isBlank(beneficiarioForm.primerApellido))
      next.primerApellido = "Campo obligatorio";
    if (isBlank(beneficiarioForm.parentesco))
      next.parentesco = "Campo obligatorio";
    setBeneficiarioErrors(next);
    return next;
  };

  const saveBeneficiario = () => {
    if (Object.keys(validateBeneficiario()).length > 0) return;
    const normalized = {
      id: editBeneficiarioId || Date.now(),
      ...beneficiarioForm,
      dni: beneficiarioForm.dni.trim(),
      primerNombre: beneficiarioForm.primerNombre.trim(),
      segundoNombre: (beneficiarioForm.segundoNombre || "").trim(),
      primerApellido: beneficiarioForm.primerApellido.trim(),
      segundoApellido: (beneficiarioForm.segundoApellido || "").trim(),
      parentesco: String(beneficiarioForm.parentesco || "").trim(),
      tipoDocumento: String(beneficiarioForm.tipoDocumento || "").trim(),
      porcentaje: `${beneficiarioForm.porcentaje ?? "0"}`.trim() || "0",
    };
    if (editBeneficiarioId) {
      setBeneficiarios((prev) =>
        prev.map((row) => (row.id === editBeneficiarioId ? normalized : row)),
      );
    } else {
      setBeneficiarios((prev) => [...prev, normalized]);
    }
    setOpenBeneficiario(false);
    resetBeneficiarioForm();
  };

  const removeBeneficiario = (id) => {
    setBeneficiarios((prev) => prev.filter((x) => x.id !== id));
  };

  const handleCambiarFoto = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    if (fotoInputRef.current) fotoInputRef.current.value = "";
    const personaId = form.personaIdInterno;
    if (!personaId) {
      setFotoError("No se puede identificar a la persona.");
      return;
    }
    try {
      setUploadingFoto(true);
      setFotoError("");
      const result = await subirFotoPersona(authToken, personaId, archivo);
      if (result?.imagen_data_url) {
        setFotoPersonaUrl(result.imagen_data_url);
      }
    } catch (err) {
      setFotoError(err?.message || "No se pudo subir la foto.");
    } finally {
      setUploadingFoto(false);
    }
  };

  // ── Funciones de cámara para actualizar foto ────────────────────────────────
  const openActualizarCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(
        "Este navegador no soporta cámara o la página no está en HTTPS/localhost.",
      );
      return;
    }
    try {
      setCameraError("");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 720 },
          height: { ideal: 960 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setCameraStream(stream);
      setCameraOpen(true);
    } catch (err) {
      setCameraError(
        "No se pudo acceder a la cámara: " + (err.message || String(err)),
      );
    }
  };

  const stopActualizarCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraStream(null);
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
  };

  const captureActualizarPhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 960;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setCapturedPhotoUrl(canvas.toDataURL("image/jpeg", 0.92));
    stopActualizarCamera();
  };

  const handleSaveCapturedPhoto = async () => {
    if (!capturedPhotoUrl) return;
    const personaId = form.personaIdInterno;
    if (!personaId) {
      setFotoError("No se puede identificar a la persona.");
      return;
    }
    try {
      setUploadingFoto(true);
      setFotoError("");
      const [header, b64data] = capturedPhotoUrl.split(",");
      const mime = header.match(/:(.*?);/)[1];
      const binary = atob(b64data);
      const arr = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      const file = new File([blob], "foto_capturada.jpg", {
        type: "image/jpeg",
      });
      const result = await subirFotoPersona(authToken, personaId, file);
      if (result?.imagen_data_url) setFotoPersonaUrl(result.imagen_data_url);
      setCapturedPhotoUrl("");
      setCameraDialogOpen(false);
    } catch (err) {
      setFotoError(err?.message || "No se pudo guardar la foto.");
    } finally {
      setUploadingFoto(false);
    }
  };

  // Asigna el stream al elemento <video> cuando el diálogo está abierto
  useEffect(() => {
    if (!cameraOpen || !cameraStream || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = cameraStream;
    video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [cameraOpen, cameraStream]);

  // Detiene la cámara si el diálogo se cierra
  useEffect(() => {
    if (!cameraDialogOpen) stopActualizarCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraDialogOpen]);
  // ────────────────────────────────────────────────────────────────────────────

  const resetReferenciaForm = () => {
    setReferenciaForm({
      referenciaId: null,
      nombreCompleto: "",
      telefono: "",
      direccion: "",
      tipoReferencia: "",
      FKReferenciaTipoId: "",
    });
    setReferenciaErrors({});
    setEditReferenciaId(null);
  };

  const openNuevaReferencia = () => {
    resetReferenciaForm();
    setOpenReferencia(true);
  };

  const openEditarReferencia = (item) => {
    setReferenciaForm({
      referenciaId: item?.referenciaId || null,
      nombreCompleto: item?.nombreCompleto || "",
      telefono: item?.telefono || "",
      direccion: item?.direccion || "",
      tipoReferencia: item?.tipoReferencia || "",
      FKReferenciaTipoId: item?.FKReferenciaTipoId || "",
    });
    setReferenciaErrors({});
    setEditReferenciaId(item?.id);
    setOpenReferencia(true);
  };

  const validateReferencia = () => {
    const next = {};
    if (isBlank(referenciaForm.nombreCompleto))
      next.nombreCompleto = "Campo obligatorio";
    if (isBlank(referenciaForm.telefono)) next.telefono = "Campo obligatorio";
    if (
      isBlank(referenciaForm.FKReferenciaTipoId) &&
      isBlank(referenciaForm.tipoReferencia)
    ) {
      next.tipoReferencia = "Campo obligatorio";
    }
    if (referenciaDireccionAplica() && isBlank(referenciaForm.direccion)) {
      next.direccion = "Campo obligatorio";
    }
    setReferenciaErrors(next);
    return next;
  };

  const saveReferencia = () => {
    if (Object.keys(validateReferencia()).length > 0) return;
    const tipoId = String(referenciaForm.FKReferenciaTipoId || "").trim();
    const tipoNombre =
      getCatalogLabel(
        catalogos.referenciaTipos,
        "ReferenciaId",
        "Nombre",
        tipoId,
      ) ||
      referenciaForm.tipoReferencia ||
      "";
    const requiereDireccion = referenciaDireccionAplica();
    const normalized = {
      id: editReferenciaId || Date.now(),
      referenciaId: referenciaForm.referenciaId || null,
      nombreCompleto: referenciaForm.nombreCompleto.trim(),
      telefono: referenciaForm.telefono.trim(),
      direccion: requiereDireccion
        ? (referenciaForm.direccion || "").trim()
        : "",
      tipoReferencia: tipoNombre,
      FKReferenciaTipoId: tipoId,
    };
    if (editReferenciaId) {
      setReferencias((prev) =>
        prev.map((row) => (row.id === editReferenciaId ? normalized : row)),
      );
    } else {
      setReferencias((prev) => [...prev, normalized]);
    }
    setOpenReferencia(false);
    resetReferenciaForm();
  };

  const removeReferencia = (id) => {
    setReferencias((prev) => prev.filter((x) => x.id !== id));
  };

  const resetPadecimientoForm = () => {
    setPadecimientoForm({ id: "", descripcion: "" });
    setPadecimientoError("");
    setEditPadecimientoId(null);
  };

  const ensurePadecimientosCatalog = async () => {
    if ((catalogos.padecimientos || []).length > 0 || !authToken) return;
    try {
      const data = await apiRequest("/Enrolamiento/catalogos/", { authToken });
      const c = data?.catalogos || {};
      if (Array.isArray(c?.padecimientos) && c.padecimientos.length > 0) {
        setCatalogos((prev) => ({ ...prev, padecimientos: c.padecimientos }));
      }
    } catch (_err) {
      // Keep silent: initial catalog load already handles errors globally.
    }
  };

  const openNuevoPadecimiento = async () => {
    await ensurePadecimientosCatalog();
    resetPadecimientoForm();
    setOpenPadecimiento(true);
  };

  const openEditarPadecimiento = async (item) => {
    await ensurePadecimientosCatalog();
    setPadecimientoForm({
      id: String(item?.id || "").trim(),
      descripcion: item?.descripcion || "",
    });
    setPadecimientoError("");
    setEditPadecimientoId(item?.id);
    setOpenPadecimiento(true);
  };

  const savePadecimiento = () => {
    const id = String(padecimientoForm.id || "").trim();
    if (!id) {
      setPadecimientoError("Campo obligatorio");
      return;
    }
    const descripcion =
      getCatalogLabel(
        catalogos.padecimientos,
        "PadecimientoMedicoId",
        "Descripcion",
        id,
      ) ||
      padecimientoForm.descripcion ||
      "";
    const normalized = {
      id,
      descripcion,
    };
    if (editPadecimientoId) {
      setPadecimientos((prev) =>
        prev.map((row) =>
          String(row.id) === String(editPadecimientoId) ? normalized : row,
        ),
      );
    } else {
      const exists = (padecimientos || []).some((row) => String(row.id) === id);
      if (exists) {
        setPadecimientoError("Este padecimiento ya existe.");
        return;
      }
      setPadecimientos((prev) => [...prev, normalized]);
    }
    setOpenPadecimiento(false);
    resetPadecimientoForm();
  };

  const removePadecimiento = (id) => {
    setPadecimientos((prev) => prev.filter((x) => String(x.id) !== String(id)));
  };

  const handleAdjuntoFileChange = (event) => {
    const file = event?.target?.files?.[0] || null;
    setAdjuntoFile(file);
    if (file && !String(adjuntoDescripcion || "").trim()) {
      setAdjuntoDescripcion(file.name);
    }
  };

  const handleSubirAdjunto = async () => {
    const personaId = String(
      form.personaIdInterno || form.identificacion || "",
    ).trim();
    if (!personaId) {
      setOk("");
      setMessage("Debe seleccionar un afiliado antes de adjuntar archivos.");
      return;
    }
    if (!adjuntoFile) {
      setOk("");
      setMessage("Seleccione un archivo para adjuntar.");
      return;
    }

    try {
      setSubiendoAdjunto(true);
      const formData = new FormData();
      formData.append("archivo", adjuntoFile);
      formData.append(
        "descripcion",
        String(adjuntoDescripcion || adjuntoFile.name || "").trim(),
      );
      formData.append("entidad", "AFILIACION");
      formData.append("registro", personaId);

      await apiRequest(
        `/Enrolamiento/personas/${encodeURIComponent(personaId)}/adjuntos/?entidad=afiliacion`,
        {
          authToken,
          method: "POST",
          body: formData,
        },
      );

      await fetchAdjuntosPersona(personaId);
      setAdjuntoFile(null);
      setAdjuntoDescripcion("");
      if (adjuntoInputRef.current) {
        adjuntoInputRef.current.value = "";
      }
      setMessage("");
      setOk("Adjunto cargado correctamente.");
    } catch (err) {
      setOk("");
      setMessage(err?.message || "No se pudo cargar el adjunto.");
    } finally {
      setSubiendoAdjunto(false);
    }
  };

  const resetFamiliarForm = () => {
    setFamiliarForm({
      primerNombre: "",
      segundoNombre: "",
      primerApellido: "",
      segundoApellido: "",
      parentesco: "",
      nacionalidad: "HND",
    });
    setFamiliarErrors({});
    setEditFamiliarId(null);
  };

  const resetEmpresaForm = () => {
    setEmpresaForm({
      razonSocial: "",
      rtn: "",
      pais: "HND",
      giroEntidad: "",
    });
    setEmpresaErrors({});
    setEditEmpresaId(null);
  };

  const openNuevoFamiliar = () => {
    resetFamiliarForm();
    setOpenFamiliar(true);
  };

  const openEditarFamiliar = (item) => {
    setFamiliarForm({
      primerNombre: item.primerNombre,
      segundoNombre: item.segundoNombre,
      primerApellido: item.primerApellido,
      segundoApellido: item.segundoApellido,
      parentesco: item.parentesco,
      nacionalidad: item.nacionalidad,
    });
    setFamiliarErrors({});
    setEditFamiliarId(item.id);
    setOpenFamiliar(true);
  };

  const validateFamiliar = () => {
    const next = {};
    if (isBlank(familiarForm.primerNombre))
      next.primerNombre = "Campo obligatorio";
    if (isBlank(familiarForm.primerApellido))
      next.primerApellido = "Campo obligatorio";
    if (isBlank(familiarForm.parentesco)) next.parentesco = "Campo obligatorio";
    if (isBlank(familiarForm.nacionalidad))
      next.nacionalidad = "Campo obligatorio";
    setFamiliarErrors(next);
    return next;
  };

  const saveFamiliar = () => {
    if (Object.keys(validateFamiliar()).length > 0) return;
    if (editFamiliarId) {
      setFamiliaresPpe((prev) =>
        prev.map((x) =>
          x.id === editFamiliarId ? { ...x, ...familiarForm } : x,
        ),
      );
    } else {
      setFamiliaresPpe((prev) => [
        ...prev,
        { id: Date.now(), ...familiarForm },
      ]);
    }
    setOpenFamiliar(false);
    resetFamiliarForm();
  };

  const removeFamiliar = (id) => {
    setFamiliaresPpe((prev) => prev.filter((x) => x.id !== id));
  };

  const openNuevaEmpresa = () => {
    resetEmpresaForm();
    setOpenEmpresa(true);
  };

  const openEditarEmpresa = (item) => {
    setEmpresaForm({
      razonSocial: item.razonSocial,
      rtn: item.rtn,
      pais: item.pais,
      giroEntidad: item.giroEntidad,
    });
    setEmpresaErrors({});
    setEditEmpresaId(item.id);
    setOpenEmpresa(true);
  };

  const validateEmpresa = () => {
    const next = {};
    if (isBlank(empresaForm.razonSocial))
      next.razonSocial = "Campo obligatorio";
    if (isBlank(empresaForm.rtn)) next.rtn = "Campo obligatorio";
    if (isBlank(empresaForm.pais)) next.pais = "Campo obligatorio";
    if (isBlank(empresaForm.giroEntidad))
      next.giroEntidad = "Campo obligatorio";
    setEmpresaErrors(next);
    return next;
  };

  const saveEmpresa = () => {
    if (Object.keys(validateEmpresa()).length > 0) return;
    if (editEmpresaId) {
      setEmpresasPpe((prev) =>
        prev.map((x) =>
          x.id === editEmpresaId ? { ...x, ...empresaForm } : x,
        ),
      );
    } else {
      setEmpresasPpe((prev) => [...prev, { id: Date.now(), ...empresaForm }]);
    }
    setOpenEmpresa(false);
    resetEmpresaForm();
  };

  const removeEmpresa = (id) => {
    setEmpresasPpe((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.4 },
        background:
          "linear-gradient(180deg, rgba(25,73,140,0.08) 0%, rgba(173,132,241,0.09) 100%)",
        minHeight: "calc(100vh - 90px)",
      }}
    >
      <Paper
        sx={{
          p: { xs: 1.6, md: 2.2 },
          border: "1px solid #dbe6f6",
          borderRadius: 3,
          boxShadow: "0 12px 30px rgba(25,73,140,0.12)",
          mb: 1.6,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1.4, color: UI_PRIMARY }}>
          Buscar Afiliado (DNI o Primer Nombre y Primer Apellido)
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.1fr 1fr 1fr auto auto" },
            gap: 1,
            alignItems: "end",
          }}
        >
          <TextField
            size="small"
            label="DNI"
            value={formatIdentificacionVisual(dniBusqueda)}
            onChange={(event) =>
              setDniBusqueda(stripIdentificacionVisual(event.target.value))
            }
            inputProps={{ maxLength: 15 }}
          />
          <TextField
            size="small"
            label="Primer nombre"
            value={primerNombreBusqueda}
            onChange={(event) => setPrimerNombreBusqueda(event.target.value)}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            size="small"
            label="Primer apellido"
            value={primerApellidoBusqueda}
            onChange={(event) => setPrimerApellidoBusqueda(event.target.value)}
            inputProps={{ maxLength: 100 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleBuscar}
            disabled={searching}
            sx={{
              backgroundColor: UI_PRIMARY,
              color: "#fff",
              borderRadius: 2.5,
            }}
          >
            {searching ? "Buscando..." : "Buscar"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleLimpiarBusqueda}
            sx={{
              borderColor: UI_PRIMARY,
              color: UI_PRIMARY,
              borderRadius: 2.5,
            }}
          >
            Limpiar
          </Button>
        </Box>
        <Typography sx={{ mt: 0.8, color: "#5d6d85", fontSize: 13 }}>
          Use solo una opción: DNI o Primer Nombre + Primer Apellido.
        </Typography>
        {searchError && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {searchError}
          </Alert>
        )}
        {searchResults.length > 0 && (
          <Box
            sx={{
              mt: 1.2,
              border: "1px solid #dbe6f6",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    >
                      No. Identificación
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    >
                      Nombres
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    >
                      Apellidos
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    >
                      Fecha de Nacimiento
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    >
                      Nacionalidad
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    >
                      Detalle
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((row) => (
                    <TableRow
                      key={`${row?.PersonaId || ""}-${row?.DocumentoNumero || ""}`}
                    >
                      <TableCell>
                        {formatIdentificacionVisual(
                          row?.DocumentoNumero || row?.PersonaId || "",
                        )}
                      </TableCell>
                      <TableCell>
                        {[row?.Nombre1, row?.Nombre2].filter(Boolean).join(" ")}
                      </TableCell>
                      <TableCell>
                        {[row?.Apellido1, row?.Apellido2]
                          .filter(Boolean)
                          .join(" ")}
                      </TableCell>
                      <TableCell>
                        {formatSearchDate(row?.FechaNacimiento)}
                      </TableCell>
                      <TableCell>
                        {row?.NacionalidadDescripcion ||
                          row?.FKNacionalidadId ||
                          ""}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleSeleccionarResultado(row)}
                          disabled={searching}
                        >
                          <VisibilityIcon fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {mostrarFormulario && (
        <>
          <Paper
            sx={{
              p: { xs: 1.6, md: 2.2 },
              border: "1px solid #dbe6f6",
              borderRadius: 3,
              boxShadow: "0 12px 30px rgba(25,73,140,0.12)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
                gap: 1.6,
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: 175,
                    height: 200,
                    border: "1px solid #d0d0d0",
                    borderRadius: 1,
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: "#f8f8f8",
                    mx: "auto",
                    overflow: "hidden",
                  }}
                >
                  {fotoPersonaUrl ? (
                    <img
                      src={fotoPersonaUrl}
                      alt="Foto de afiliado"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center top",
                      }}
                    />
                  ) : (
                    <PersonIcon sx={{ fontSize: 72, color: "#7c7c7c" }} />
                  )}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    disabled={uploadingFoto || !form.personaIdInterno}
                    onClick={() => {
                      setCapturedPhotoUrl("");
                      setCameraDialogOpen(true);
                      openActualizarCamera();
                    }}
                    sx={{ fontSize: 12 }}
                  >
                    {uploadingFoto ? "Guardando..." : "Tomar foto"}
                  </Button>
                </Box>
                {fotoError && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "error.main",
                      textAlign: "center",
                      mt: 0.5,
                    }}
                  >
                    {fotoError}
                  </Typography>
                )}
                <Typography sx={{ mt: 5, fontSize: 14 }}>
                  Fecha actualizo información
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={fechaActualizacionInfo}
                  disabled
                />
                <Typography sx={{ mt: 1, fontSize: 14 }}>
                  Días transcurridos
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={diasTranscurridosInfo}
                  disabled
                />
              </Box>
              <Box sx={{ pt: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                    gap: 1.4,
                  }}
                >
                  {selectInput({
                    name: "tipoDocumento",
                    label: "(*) Tipo de documento",
                    options: catalogos.documentoTipos,
                    valueKey: "DocumentoTipoId",
                    labelKey: "Descripcion",
                    disabled: loadingCatalogos,
                  })}
                  {input("primerNombre", "(*) Primer nombre")}
                  {input("segundoNombre", "Segundo nombre")}
                  {input("identificacion", "(*) Número de identificación", {
                    InputProps: { readOnly: true },
                  })}
                  {input("primerApellido", "(*) Primer apellido")}
                  {input("segundoApellido", "Segundo apellido")}
                  {input("fechaNacimiento", "(*) Fecha de nacimiento", {
                    type: "date",
                    InputLabelProps: { shrink: true },
                  })}
                  {selectInput({
                    name: "paisNacimiento",
                    label: "(*) País de nacimiento",
                    options: catalogos.nacionalidades,
                    valueKey: "NacionalidadId",
                    labelKey: "Descripcion",
                    disabled: loadingCatalogos,
                  })}
                  {selectInput({
                    name: "nacionalidad",
                    label: "(*) Nacionalidad",
                    options: catalogos.nacionalidades,
                    valueKey: "NacionalidadId",
                    labelKey: "GentilicioDisplay",
                    disabled: loadingCatalogos,
                  })}
                  {selectInput({
                    name: "genero",
                    label: "(*) Sexo",
                    options: catalogos.sexos,
                    valueKey: "SexoId",
                    labelKey: "Descripcion",
                    disabled: loadingCatalogos,
                  })}
                  {selectInput({
                    name: "estadoCivil",
                    label: "(*) Estado civil",
                    options: catalogos.estadosCiviles,
                    valueKey: "EstadoCivilId",
                    labelKey: "Descripcion",
                    disabled: loadingCatalogos,
                  })}
                  {input("rtn", "RTN")}
                  {input("lugarNacimiento", "(*) Lugar de nacimiento")}
                  <Autocomplete
                    options={profesionesSearch}
                    getOptionLabel={(option) =>
                      String(
                        option?.Descripcion ?? option?.ProfesionOficioId ?? "",
                      ).trim()
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option?.ProfesionOficioId ?? "").trim() ===
                      String(value?.ProfesionOficioId ?? "").trim()
                    }
                    value={
                      profesionesSearch.find(
                        (option) =>
                          String(option?.ProfesionOficioId ?? "").trim() ===
                          String(form.profesion ?? "").trim(),
                      ) || null
                    }
                    onChange={(_event, value) => {
                      setFieldValue(
                        "profesion",
                        String(value?.ProfesionOficioId ?? "").trim(),
                      );
                    }}
                    disabled={loadingCatalogos}
                    noOptionsText="Sin coincidencias"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="(*) Profesión u oficio"
                        error={Boolean(errors.profesion)}
                        helperText={errors.profesion || ""}
                      />
                    )}
                  />
                  <Autocomplete
                    options={actividadEconomicaCnbsSearchOptions}
                    filterOptions={filterActividadEconomicaCnbsOptions}
                    getOptionLabel={(option) =>
                      String(option ?? "").trim().toUpperCase()
                    }
                    isOptionEqualToValue={(option, value) =>
                      normalizeActividadEconomicaFilter(
                        String(option ?? "").trim(),
                      ) ===
                        normalizeActividadEconomicaFilter(
                          String(value ?? "").trim(),
                        )
                    }
                    value={actividadEconomicaCnbsCurrentLabel || null}
                    onChange={(_event, value) => {
                      handleActividadEconomicaCnbsSelect(value);
                    }}
                    onInputChange={(_event, _value, reason) => {
                      if (reason === "clear") handleActividadEconomicaCnbsSelect("");
                    }}
                    disabled={loadingCatalogos}
                    noOptionsText="Sin coincidencias"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="(*) Actividad económica CNBS"
                        error={Boolean(errors.actividadEconomicaCnbs)}
                        helperText={errors.actividadEconomicaCnbs || ""}
                      />
                    )}
                  />
                  <Autocomplete
                    options={profesionesCnbsSearch}
                    getOptionLabel={(option) =>
                      String(
                        option?.NombreProfesionCnbs ?? option?.ProfesionCnbsID ?? "",
                      )
                        .trim()
                        .toUpperCase()
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option?.ProfesionCnbsID ?? "").trim() ===
                      String(value?.ProfesionCnbsID ?? "").trim()
                    }
                    value={
                      profesionesCnbsSearch.find(
                        (option) =>
                          String(option?.ProfesionCnbsID ?? "").trim() ===
                          String(form.profesionCnbs ?? "").trim(),
                      ) || null
                    }
                    onChange={(_event, value) => {
                      setFieldValue(
                        "profesionCnbs",
                        String(value?.ProfesionCnbsID ?? "").trim(),
                      );
                    }}
                    disabled={loadingCatalogos}
                    noOptionsText="Sin coincidencias"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="(*) Profesión CNBS"
                        error={Boolean(errors.profesionCnbs)}
                        helperText={errors.profesionCnbs || ""}
                      />
                    )}
                  />
                  {selectInput({
                    name: "nivelEducativo",
                    label: "(*) Nivel educativo",
                    options: catalogos.nivelesEducativos,
                    valueKey: "NivelEducativoId",
                    labelKey: "Descripcion",
                    disabled: loadingCatalogos,
                  })}
                  {catalogos.tipoClientes?.length
                    ? selectInput({
                        name: "tipoCliente",
                        label: "(*) Tipo de cliente",
                        options: catalogos.tipoClientes,
                        valueKey: "TipoClienteSolicitanteId",
                        labelKey: "Descripcion",
                        disabled: loadingCatalogos,
                      })
                    : input("tipoCliente", "(*) Tipo de cliente")}
                  {input(
                    "dependientesEconomicos",
                    "(*) Número de dependientes",
                    { type: "number" },
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.esPep}
                        onChange={setField("esPep")}
                      />
                    }
                    label="Persona politicamente expuesta (restringido)"
                  />
                </Box>
                <Box sx={{ borderTop: "1px solid #dadada", mt: 1.5, pt: 1 }}>
                  <Typography sx={{ mb: 1 }}>
                    Información del conyuge
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    {selectInput({
                      name: "tipoDocumentoConyuge",
                      label: "Tipo de documento cónyuge",
                      options: catalogos.documentoTipos,
                      valueKey: "DocumentoTipoId",
                      labelKey: "Descripcion",
                      disabled: loadingCatalogos,
                    })}
                    {input(
                      "identificacionConyuge",
                      "Número de identificación cónyuge",
                    )}
                    {input("nombreConyuge", "Nombre completo cónyuge")}
                    {input(
                      "fechaNacimientoConyuge",
                      "Fecha de nacimiento cónyuge",
                      { type: "date", InputLabelProps: { shrink: true } },
                    )}
                    {selectInput({
                      name: "generoConyuge",
                      label: "Sexo cónyuge",
                      options: catalogos.sexos,
                      valueKey: "SexoId",
                      labelKey: "Descripcion",
                      disabled: loadingCatalogos,
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper
            sx={{
              p: { xs: 1.2, md: 1.8 },
              mt: 3,
              border: "1px solid #dbe6f6",
              borderRadius: 3,
              boxShadow: "0 12px 30px rgba(25,73,140,0.12)",
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  minHeight: 38,
                  color: "#fff",
                  backgroundColor: UI_PRIMARY,
                  mr: 0.4,
                  borderRadius: "10px 10px 0 0",
                  px: 1.6,
                },
                "& .MuiTab-root.Mui-selected": {
                  backgroundColor: UI_GOLD,
                  color: "#fff !important",
                  fontWeight: 700,
                },
                "& .MuiTab-root.Mui-selected:hover": {
                  backgroundColor: UI_GOLD,
                  color: "#fff !important",
                },
              }}
            >
              {headers.map((h) => (
                <Tab key={h} label={h} />
              ))}
            </Tabs>
            <Box
              sx={{
                border: "1px solid #dbe6f6",
                borderRadius: 2,
                p: { xs: 1.2, md: 1.8 },
                minHeight: 280,
                backgroundColor: "#fff",
              }}
            >
              {activeHeader === "RESIDENCIAL" && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    columnGap: 1.4,
                    rowGap: 1.8,
                  }}
                >
                  {selectInput({
                    name: "departamentoResidencia",
                    label: "(*) Departamento de residencia",
                    options: catalogos.departamentos,
                    valueKey: "DepartamentoId",
                    labelKey: "Nombre",
                    onChange: handleDepartamentoResidenciaChange,
                    disabled: loadingCatalogos,
                  })}
                  {input("telefono", "Número de teléfono")}
                  {selectInput({
                    name: "municipioResidencia",
                    label: "(*) Municipio de residencia",
                    options: municipiosResidencia,
                    valueKey: "MunicipioId",
                    labelKey: "Nombre",
                    onChange: handleMunicipioResidenciaChange,
                    disabled: loadingCatalogos,
                  })}
                  {input("movil", "(*) Número de celular")}
                  {coloniaResidenciaOptions.length > 0 ? (
                    <Autocomplete
                      options={coloniaResidenciaOptions}
                      value={coloniaResidenciaDisplayValue}
                      onChange={(_event, value) =>
                        handleColoniaResidenciaChange(value)
                      }
                      onInputChange={(_event, _value, reason) => {
                        if (reason === "clear") handleColoniaResidenciaChange("");
                      }}
                      disabled={loadingCatalogos || !form.municipioResidencia}
                      noOptionsText="Sin coincidencias"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="(*) Colonia de residencia"
                          error={Boolean(errors.coloniaResidencia)}
                          helperText={errors.coloniaResidencia || ""}
                        />
                      )}
                    />
                  ) : (
                    input("coloniaResidencia", "(*) Colonia de residencia")
                  )}
                  {input("tiempoResidir", "(*) Tiempo de residir (años)", {
                    type: "number",
                    inputProps: { min: 0, max: MAX_TIEMPO_RESIDIR },
                    onChange: handleTiempoResidirChange,
                  })}
                  {input("direccionCompleta", "(*) Dirección completa", {
                    multiline: true,
                    minRows: 2,
                  })}
                  {input("fechaDesdeReside", "(*) Fecha desde que reside", {
                    type: "date",
                    InputLabelProps: { shrink: true },
                    onChange: handleFechaDesdeResideChange,
                  })}
                  {input("calle", "Número de calle")}
                  {input("numeroCasa", "Número de casa")}
                  {input("referenciaDireccion", "Referencia de ubicación", {
                    multiline: true,
                    minRows: 2,
                  })}
                  {input("correo", "Correo electrónico", {
                    helperText: errors.correo || "Ej: usuario@dominio.com (no se permite la ñ)",
                    onBlur: handleCorreoBlur,
                  })}
                </Box>
              )}
              {activeHeader === "LABORAL" && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                    columnGap: 1.4,
                    rowGap: 1.8,
                  }}
                >
                  {input("patrono", "(*) Nombre del empleador / patrono")}
                  {input(
                    "fechaIngresoInstitucion",
                    "(*) Fecha de ingreso a la institución",
                    { type: "date", InputLabelProps: { shrink: true } },
                  )}
                  {input("antiguedadLaboral", "(*) Antigüedad laboral", {
                    InputProps: { readOnly: true },
                  })}
                  {selectInput({
                    name: "departamentoTrabajo",
                    label: "(*) Departamento donde trabaja",
                    options: catalogos.departamentos,
                    valueKey: "DepartamentoId",
                    labelKey: "Nombre",
                    onChange: handleDepartamentoTrabajoChange,
                    disabled: loadingCatalogos,
                  })}
                  {input("telefonoTrabajo", "Número de teléfono del trabajo")}
                  {selectInput({
                    name: "ingresoAproxRango",
                    label: "(*) Ingresos aproximados",
                    options: catalogos.ingresosAproximados,
                    valueKey: "IngresosAproximadosId",
                    labelKey: "Ingresos",
                    disabled: loadingCatalogos,
                  })}
                  {selectInput({
                    name: "municipioTrabajo",
                    label: "(*) Municipio donde trabaja",
                    options: municipiosTrabajo,
                    valueKey: "MunicipioId",
                    labelKey: "Nombre",
                    onChange: handleMunicipioTrabajoChange,
                    disabled: loadingCatalogos,
                  })}
                  {selectInput({
                    name: "giroEntidad",
                    label: "(*) Giro de la entidad",
                    options: catalogos.girosEntidad,
                    valueKey: "GiroEntidadId",
                    labelKey: "Descripcion",
                    disabled: loadingCatalogos,
                  })}
                  {input(
                    "ingresoAproxMonto",
                    "(*) Ingresos aproximados (monto)",
                  )}
                  {coloniaTrabajoOptions.length > 0 ? (
                    <Autocomplete
                      options={coloniaTrabajoOptions}
                      value={coloniaTrabajoDisplayValue}
                      onChange={(_event, value) =>
                        handleColoniaTrabajoChange(value)
                      }
                      onInputChange={(_event, _value, reason) => {
                        if (reason === "clear") handleColoniaTrabajoChange("");
                      }}
                      disabled={loadingCatalogos || !form.municipioTrabajo}
                      noOptionsText="Sin coincidencias"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="(*) Colonia donde trabaja"
                          error={Boolean(errors.coloniaTrabajo)}
                          helperText={errors.coloniaTrabajo || ""}
                        />
                      )}
                    />
                  ) : (
                    input("coloniaTrabajo", "(*) Colonia donde trabaja")
                  )}
                  {input("numeroCasaTrabajo", "Número de casa")}
                  <Autocomplete
                    options={cargosSearch}
                    filterOptions={filterCargoOptions}
                    getOptionLabel={(option) =>
                      String(option?.Descripcion ?? option?.CargoId ?? "")
                        .trim()
                        .toUpperCase()
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option?.CargoId ?? "").trim() ===
                      String(value?.CargoId ?? "").trim()
                    }
                    value={
                      cargosSearch.find(
                        (option) =>
                          String(option?.CargoId ?? "").trim() ===
                          String(form.cargoDesempenado ?? "").trim(),
                      ) || null
                    }
                    onChange={(_event, value) => {
                      setFieldValue(
                        "cargoDesempenado",
                        String(value?.CargoId ?? "").trim(),
                      );
                    }}
                    onInputChange={(_event, _value, reason) => {
                      if (reason === "clear") setFieldValue("cargoDesempenado", "");
                    }}
                    disabled={loadingCatalogos}
                    noOptionsText="Sin coincidencias"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="(*) Cargo desempeñado"
                        error={Boolean(errors.cargoDesempenado)}
                        helperText={errors.cargoDesempenado || ""}
                      />
                    )}
                  />
                  {input("referenciaTrabajo", "Referencia de ubicación", {
                    multiline: true,
                    minRows: 2,
                  })}
                  {input("direccionTrabajo", "Dirección de trabajo", {
                    multiline: true,
                    minRows: 2,
                  })}
                  {input("areaTrabajo", "Nombre del área del trabajo")}
                </Box>
              )}
              {activeHeader === "PATRIMONIO" && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    columnGap: 1.4,
                    rowGap: 1.8,
                  }}
                >
                  <Paper
                    sx={{
                      p: 1.5,
                      boxShadow: "none",
                      border: "1px solid #dbe6f6",
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: "grid", rowGap: 1.4 }}>
                      {selectInput({
                        name: "tipoVivienda",
                        label: "(*) Tipo de vivienda",
                        options: catalogos.viviendaTipos,
                        valueKey: "ViviendaTipoId",
                        labelKey: "Descripcion",
                        disabled: loadingCatalogos,
                      })}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.poseeVehiculo}
                            onChange={setField("poseeVehiculo")}
                          />
                        }
                        label="Posee vehículo"
                      />
                      {form.poseeVehiculo &&
                        input("numeroVehiculos", "Número de vehículos", {
                          type: "number",
                          inputProps: { min: 1 },
                        })}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.poseeInmuebles}
                            onChange={setField("poseeInmuebles")}
                          />
                        }
                        label="Otros inmuebles"
                      />
                      {form.poseeInmuebles &&
                        input(
                          "descOtrosInmuebles",
                          "Descripción de otros inmuebles",
                          {
                            multiline: true,
                            minRows: 2,
                          },
                        )}
                    </Box>
                  </Paper>
                  <Paper
                    sx={{
                      p: 1.5,
                      boxShadow: "none",
                      border: "1px solid #dbe6f6",
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: "grid", rowGap: 1.4 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.otrosIngresos}
                            onChange={setField("otrosIngresos")}
                          />
                        }
                        label="Otros ingresos"
                      />
                      {form.otrosIngresos &&
                        input(
                          "descOtrosIngresos",
                          "Descripción de otros ingresos",
                          {
                            multiline: true,
                            minRows: 2,
                          },
                        )}
                      {form.otrosIngresos &&
                        input(
                          "montoOtrosIngresos",
                          "Monto aproximado de otros ingresos",
                          {
                            type: "number",
                            inputProps: { min: 0 },
                          },
                        )}
                    </Box>
                  </Paper>
                </Box>
              )}
              {activeHeader === "BENEFICIARIOS" && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: "#183d73" }}>
                      Beneficiarios Potenciales
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={openNuevoBeneficiario}
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        borderRadius: 2.2,
                        minWidth: 88,
                      }}
                    >
                      Nuevo
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            No. Identificación
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Primer Nombre
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Segundo Nombre
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Primer Apellido
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Segundo Apellido
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Parentesco
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          />
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {beneficiarios.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ color: "#6f6f6f" }}>
                              Sin datos para mostrar
                            </TableCell>
                          </TableRow>
                        ) : (
                          beneficiarios.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                {formatIdentificacionVisual(item.dni)}
                              </TableCell>
                              <TableCell>{item.primerNombre}</TableCell>
                              <TableCell>{item.segundoNombre}</TableCell>
                              <TableCell>{item.primerApellido}</TableCell>
                              <TableCell>{item.segundoApellido}</TableCell>
                              <TableCell>
                                {getCatalogLabel(
                                  catalogos.parentescos,
                                  "ParentescoId",
                                  "Descripcion",
                                  item.parentesco,
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditarBeneficiario(item)}
                                >
                                  <VisibilityIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => removeBeneficiario(item.id)}
                                >
                                  <DeleteOutlineIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {activeHeader === "REFERENCIAS" && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: "#183d73" }}>
                      Referencias
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={openNuevaReferencia}
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        borderRadius: 2.2,
                        minWidth: 88,
                      }}
                    >
                      Nuevo
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Nombre completo
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Teléfono
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Dirección
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Tipo de referencia
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          />
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {referencias.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ color: "#6f6f6f" }}>
                              Sin datos para mostrar
                            </TableCell>
                          </TableRow>
                        ) : (
                          referencias.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.nombreCompleto}</TableCell>
                              <TableCell>
                                {formatTelefonoVisual(item.telefono)}
                              </TableCell>
                              <TableCell>{item.direccion}</TableCell>
                              <TableCell>{getReferenciaTipoNombre(item.FKReferenciaTipoId, item.tipoReferencia)}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditarReferencia(item)}
                                >
                                  <VisibilityIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => removeReferencia(item.id)}
                                >
                                  <DeleteOutlineIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography sx={{ mt: 1, color: "#4b4b4b", fontSize: 13 }}>
                    {getReferenciasRequirementSummary()}
                  </Typography>
                </Box>
              )}
              {activeHeader === "PADECIMIENTOS" && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: "#183d73" }}>
                      Padecimientos médicos
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={openNuevoPadecimiento}
                      sx={{
                        backgroundColor: UI_PRIMARY,
                        color: "#fff",
                        borderRadius: 2.2,
                        minWidth: 88,
                      }}
                    >
                      Nuevo
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            Padecimiento médico
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          />
                          <TableCell
                            sx={{
                              backgroundColor: UI_PRIMARY,
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {padecimientos.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ color: "#6f6f6f" }}>
                              Sin datos para mostrar
                            </TableCell>
                          </TableRow>
                        ) : (
                          padecimientos.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{getPadecimientoDescripcion(item.id, item.descripcion)}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditarPadecimiento(item)}
                                >
                                  <VisibilityIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => removePadecimiento(item.id)}
                                >
                                  <DeleteOutlineIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {activeHeader === "PPE" && (
                <Box>
                  <Box sx={{ mb: 2.2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        mb: 1,
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={openNuevoFamiliar}
                        sx={{
                          backgroundColor: UI_PRIMARY,
                          color: "#fff",
                          borderRadius: 2.2,
                          minWidth: 88,
                        }}
                      >
                        Nuevo
                      </Button>
                      <Typography sx={{ fontSize: 13 }}>
                        Favor indicar nombre de familiares hasta un segundo
                        grado de consanguinidad y afinidad:
                        Conyuge(1),Padres(2),Hermanos(3),Hermanos(4),Nietos(5).
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, mb: 0.6 }}>
                      Familiares
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              Nombre
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              Apellido
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              Parentesco
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            />
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {familiaresPpe.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                {[item.primerNombre, item.segundoNombre]
                                  .filter(Boolean)
                                  .join(" ")}
                              </TableCell>
                              <TableCell>
                                {[item.primerApellido, item.segundoApellido]
                                  .filter(Boolean)
                                  .join(" ")}
                              </TableCell>
                              <TableCell>
                                {getCatalogLabel(
                                  catalogos.parentescos,
                                  "ParentescoId",
                                  "Descripcion",
                                  item.parentesco,
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditarFamiliar(item)}
                                >
                                  <VisibilityIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => removeFamiliar(item.id)}
                                >
                                  <DeleteOutlineIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        mb: 1,
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={openNuevaEmpresa}
                        sx={{
                          backgroundColor: UI_PRIMARY,
                          color: "#fff",
                          borderRadius: 2.2,
                          minWidth: 88,
                        }}
                      >
                        Nuevo
                      </Button>
                      <Typography sx={{ fontSize: 13 }}>
                        ***Se aplica la Relación de Empresas si la persona
                        mantiene más del 25% de participación de la empresa***
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, mb: 0.6 }}>
                      Relación de empresas
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              Razón social
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              RTN
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              Giro de la entidad
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              País
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            />
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {empresasPpe.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.razonSocial}</TableCell>
                              <TableCell>{item.rtn}</TableCell>
                              <TableCell>
                                {getCatalogLabel(
                                  catalogos.girosEntidad,
                                  "GiroEntidadId",
                                  "Descripcion",
                                  item.giroEntidad,
                                )}
                              </TableCell>
                              <TableCell>
                                {getCatalogLabel(
                                  catalogos.nacionalidades,
                                  "NacionalidadId",
                                  "Descripcion",
                                  item.pais,
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditarEmpresa(item)}
                                >
                                  <VisibilityIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => removeEmpresa(item.id)}
                                >
                                  <DeleteOutlineIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              )}
              {activeHeader === "PERFIL LAFT" && (
                <Box sx={{ maxWidth: 360 }}>
                  {selectInput({
                    name: "perfilLaft",
                    label: "(*) Perfil LAFT",
                    options: catalogos.perfilesLaft,
                    valueKey: "PerfilLAFTId",
                    labelKey: "Descripcion",
                    disabled: loadingCatalogos,
                  })}
                </Box>
              )}
              {activeHeader === "OTROS" && (
                <Box sx={{ display: "grid", gap: 1.4 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                      gap: 1.2,
                      mt: 0.2,
                    }}
                  >
                    <TextField
                      size="small"
                      label="Usuario que realizó el registro"
                      value={form.userRegistroNombre || form.userIdRegistro || ""}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      size="small"
                      label="Oficina donde registró"
                      value={form.oficinaIdRegistroNombre || form.oficinaIdRegistro || ""}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                    {form.userIdActualizo ? (
                      <TextField
                        size="small"
                        label="Usuario que actualizó"
                        value={form.userActualizoNombre || form.userIdActualizo}
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      mt: 0.2,
                      border: "1px dashed #b9cbe8",
                      borderRadius: 2,
                      p: 1.2,
                      backgroundColor: "#f7fbff",
                    }}
                  >
                    <Typography
                      sx={{ color: UI_PRIMARY, fontWeight: 700, fontSize: 14 }}
                    >
                      Adjuntos
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.8,
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "1.5fr auto auto",
                        },
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <TextField
                        size="small"
                        label="Descripción del adjunto"
                        value={adjuntoDescripcion}
                        onChange={(e) => setAdjuntoDescripcion(e.target.value)}
                      />
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                        sx={{
                          borderColor: UI_PRIMARY,
                          color: UI_PRIMARY,
                          borderRadius: 2.1,
                        }}
                      >
                        Seleccionar archivo
                        <input
                          hidden
                          ref={adjuntoInputRef}
                          type="file"
                          onChange={handleAdjuntoFileChange}
                        />
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubirAdjunto}
                        disabled={subiendoAdjunto}
                        sx={{
                          backgroundColor: UI_PRIMARY,
                          color: "#fff",
                          borderRadius: 2.1,
                        }}
                      >
                        {subiendoAdjunto ? "Adjuntando..." : "Adjuntar"}
                      </Button>
                    </Box>
                    <Typography
                      sx={{ color: "#4b5f82", fontSize: 12, mt: 0.5 }}
                    >
                      {adjuntoFile
                        ? `Archivo seleccionado: ${adjuntoFile.name}`
                        : "Puede adjuntar uno o varios archivos."}
                    </Typography>

                    <TableContainer sx={{ mt: 0.8 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              Descripción
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              Fecha de creación
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              Tipo
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: UI_PRIMARY,
                                color: "#fff",
                                fontWeight: 700,
                                textAlign: "center",
                              }}
                            >
                              Ver adjunto
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cargandoAdjuntos ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                Cargando adjuntos...
                              </TableCell>
                            </TableRow>
                          ) : adjuntos.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                Sin datos para mostrar
                              </TableCell>
                            </TableRow>
                          ) : (
                            adjuntos.map((item) => (
                              <TableRow key={`adj-${item.id}`}>
                                <TableCell>
                                  {item.descripcion || item.nombre || "-"}
                                </TableCell>
                                <TableCell>
                                  {formatSearchDate(item.fechaCrea)}
                                </TableCell>
                                <TableCell>{item.tipo || "-"}</TableCell>
                                <TableCell align="center">
                                  {!["DESIGNACION", "BENEFICIARIO"].includes(
                                    String(item?.entidad || "").trim().toUpperCase(),
                                  ) && (
                                    <IconButton
                                      size="small"
                                      disabled={!item.downloadUrl}
                                      onClick={() =>
                                        handleOpenAdjuntoPreview(item)
                                      }
                                    >
                                      <VisibilityIcon fontSize="inherit" />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ mt: 1.2, display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  backgroundColor: UI_PRIMARY,
                  color: "#fff",
                  borderRadius: 2.5,
                }}
              >
                {saving ? "Actualizando..." : "Actualizar"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.history.back()}
                sx={{
                  borderColor: UI_PRIMARY,
                  color: UI_PRIMARY,
                  borderRadius: 2.5,
                }}
              >
                Regresar
              </Button>
            </Box>
            {message && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {message}
                {missingRequiredFields.length > 0 && (
                  <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2.5 }}>
                    {missingRequiredFields.map((fieldLabel) => (
                      <li key={`missing-${fieldLabel}`}>
                        <Typography component="span">{fieldLabel}</Typography>
                      </li>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
            {ok && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {ok}
              </Alert>
            )}
            <Typography sx={{ mt: 1, color: "#4b4b4b", fontSize: 13 }}>
              Validaciones activas: campos obligatorios de datos personales,
              residencial, laboral, patrimonio y perfil LAFT; número de celular
              obligatorio.
            </Typography>
            <Typography sx={{ color: "#4b4b4b", fontSize: 13 }}>
              Total de beneficiarios potenciales: {totalBeneficiario}.
            </Typography>
          </Paper>

          <Dialog
            open={openAdjuntoPreview}
            onClose={closeAdjuntoPreview}
            fullWidth
            maxWidth="lg"
          >
            <DialogTitle sx={{ pb: 1 }}>Vista previa de adjunto</DialogTitle>
            <DialogContent dividers sx={{ minHeight: 500 }}>
              {previewAdjuntoLoading ? (
                <Typography sx={{ color: "#4b4b4b" }}>
                  Cargando adjunto...
                </Typography>
              ) : previewAdjuntoError ? (
                <Alert severity="error">{previewAdjuntoError}</Alert>
              ) : previewAdjuntoUrl ? (
                <Box sx={{ height: "72vh" }}>
                  <iframe
                    title="adjunto-preview"
                    src={previewAdjuntoUrl}
                    style={{ width: "100%", height: "100%", border: 0 }}
                  />
                  <Typography sx={{ mt: 0.6, color: "#6a778b", fontSize: 12 }}>
                    Tipo detectado: {previewAdjuntoMime || "no identificado"}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ color: "#4b4b4b" }}>
                  No hay archivo para visualizar.
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
              <Button onClick={closeAdjuntoPreview}>Cerrar</Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openBeneficiario}
            onClose={() => setOpenBeneficiario(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ pb: 1 }}>
              {editBeneficiarioId
                ? "Editar Beneficiario Potencial"
                : "Agregar Beneficiario Potencial"}
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                  pt: 0.5,
                }}
              >
                <TextField
                  size="small"
                  label="(*) No. Identificación"
                  value={formatIdentificacionVisual(beneficiarioForm.dni)}
                  onChange={(e) =>
                    setBeneficiarioForm((prev) => ({
                      ...prev,
                      dni: e.target.value.replace(_ONLY_DIGITS, ""),
                    }))
                  }
                  inputMode="numeric"
                  error={Boolean(beneficiarioErrors.dni)}
                  helperText={beneficiarioErrors.dni || ""}
                />
                <FormControl size="small">
                  <InputLabel>Tipo documento</InputLabel>
                  <Select
                    label="Tipo documento"
                    value={beneficiarioForm.tipoDocumento}
                    onChange={(e) =>
                      setBeneficiarioForm((prev) => ({
                        ...prev,
                        tipoDocumento: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {(catalogos.documentoTipos || []).map((item) => (
                      <MenuItem
                        key={`doc-ben-${item?.DocumentoTipoId}`}
                        value={String(item?.DocumentoTipoId || "").trim()}
                      >
                        {item?.Descripcion || ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="(*) Primer Nombre"
                  value={beneficiarioForm.primerNombre}
                  onChange={(e) =>
                    setBeneficiarioForm((prev) => ({
                      ...prev,
                      primerNombre: e.target.value.replace(_ONLY_LETTERS, "").toUpperCase(),
                    }))
                  }
                  error={Boolean(beneficiarioErrors.primerNombre)}
                  helperText={beneficiarioErrors.primerNombre || ""}
                />
                <TextField
                  size="small"
                  label="Segundo Nombre"
                  value={beneficiarioForm.segundoNombre}
                  onChange={(e) =>
                    setBeneficiarioForm((prev) => ({
                      ...prev,
                      segundoNombre: e.target.value.replace(_ONLY_LETTERS, "").toUpperCase(),
                    }))
                  }
                />
                <TextField
                  size="small"
                  label="(*) Primer Apellido"
                  value={beneficiarioForm.primerApellido}
                  onChange={(e) =>
                    setBeneficiarioForm((prev) => ({
                      ...prev,
                      primerApellido: e.target.value.replace(_ONLY_LETTERS, "").toUpperCase(),
                    }))
                  }
                  error={Boolean(beneficiarioErrors.primerApellido)}
                  helperText={beneficiarioErrors.primerApellido || ""}
                />
                <TextField
                  size="small"
                  label="Segundo Apellido"
                  value={beneficiarioForm.segundoApellido}
                  onChange={(e) =>
                    setBeneficiarioForm((prev) => ({
                      ...prev,
                      segundoApellido: e.target.value.replace(_ONLY_LETTERS, "").toUpperCase(),
                    }))
                  }
                />
                <FormControl
                  size="small"
                  error={Boolean(beneficiarioErrors.parentesco)}
                >
                  <InputLabel>(*) Parentesco</InputLabel>
                  <Select
                    label="(*) Parentesco"
                    value={beneficiarioForm.parentesco}
                    onChange={(e) =>
                      setBeneficiarioForm((prev) => ({
                        ...prev,
                        parentesco: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {(catalogos.parentescos || []).map((item) => (
                      <MenuItem
                        key={`par-ben-${item?.ParentescoId}`}
                        value={String(item?.ParentescoId || "").trim()}
                      >
                        {item?.Descripcion || ""}
                      </MenuItem>
                    ))}
                  </Select>
                  {beneficiarioErrors.parentesco ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "#d32f2f", ml: 1 }}
                    >
                      {beneficiarioErrors.parentesco}
                    </Typography>
                  ) : null}
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "space-between", px: 2, pb: 1.5 }}
            >
              <Typography sx={{ color: "#d32f2f", fontSize: 13 }}>
                (*) campos requeridos
              </Typography>
              <Button
                variant="contained"
                onClick={saveBeneficiario}
                sx={{
                  backgroundColor: UI_PRIMARY,
                  color: "#fff",
                  borderRadius: 2.2,
                }}
              >
                Guardar
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openReferencia}
            onClose={() => setOpenReferencia(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ pb: 1 }}>
              {editReferenciaId ? "Editar Referencia" : "Agregar Referencia"}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: "grid", gap: 1, pt: 0.5 }}>
                <FormControl
                  size="small"
                  error={Boolean(referenciaErrors.tipoReferencia)}
                >
                  <InputLabel>(*) Tipo de referencia</InputLabel>
                  <Select
                    label="(*) Tipo de referencia"
                    value={referenciaForm.FKReferenciaTipoId}
                    onChange={(e) => {
                      const nextTipoId = e.target.value;
                      const nextTipoNombre = getCatalogLabel(
                        catalogos.referenciaTipos,
                        "ReferenciaId",
                        "Nombre",
                        nextTipoId,
                      );
                      const nextTipoNormalizado =
                        normalizeReferenciaTipo(nextTipoNombre);

                      setReferenciaForm((prev) => ({
                        ...prev,
                        FKReferenciaTipoId: nextTipoId,
                        tipoReferencia: nextTipoNombre,
                        direccion:
                          nextTipoNormalizado === "COMERCIALES / BANCARIAS"
                            ? ""
                            : prev.direccion,
                      }));
                      setReferenciaErrors((prev) => {
                        const next = { ...prev };
                        delete next.tipoReferencia;
                        if (nextTipoNormalizado === "COMERCIALES / BANCARIAS") {
                          delete next.direccion;
                        }
                        return next;
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {(catalogos.referenciaTipos || []).map((item) => (
                      <MenuItem
                        key={`tipo-ref-${item?.ReferenciaId}`}
                        value={String(item?.ReferenciaId || "").trim()}
                      >
                        {item?.Nombre || ""}
                      </MenuItem>
                    ))}
                  </Select>
                  {referenciaErrors.tipoReferencia ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "#d32f2f", ml: 1 }}
                    >
                      {referenciaErrors.tipoReferencia}
                    </Typography>
                  ) : null}
                </FormControl>
                <TextField
                  size="small"
                  label="(*) Nombre completo"
                  value={referenciaForm.nombreCompleto}
                  onChange={(e) =>
                    setReferenciaForm((prev) => ({
                      ...prev,
                      nombreCompleto: e.target.value.replace(_ONLY_LETTERS_NUMS, "").toUpperCase(),
                    }))
                  }
                  error={Boolean(referenciaErrors.nombreCompleto)}
                  helperText={referenciaErrors.nombreCompleto || ""}
                />
                <TextField
                  size="small"
                  label="(*) Teléfono"
                  value={formatTelefonoVisual(referenciaForm.telefono)}
                  onChange={(e) =>
                    setReferenciaForm((prev) => ({
                      ...prev,
                      telefono: e.target.value.replace(_ONLY_DIGITS, ""),
                    }))
                  }
                  inputMode="numeric"
                  error={Boolean(referenciaErrors.telefono)}
                  helperText={referenciaErrors.telefono || ""}
                />
                {referenciaDireccionAplica() && (
                  <TextField
                    size="small"
                    label="(*) Dirección"
                    value={referenciaForm.direccion}
                    onChange={(e) =>
                      setReferenciaForm((prev) => ({
                        ...prev,
                        direccion: e.target.value.toUpperCase(),
                      }))
                    }
                    error={Boolean(referenciaErrors.direccion)}
                    helperText={referenciaErrors.direccion || ""}
                    multiline
                    minRows={2}
                  />
                )}
              </Box>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "space-between", px: 2, pb: 1.5 }}
            >
              <Typography sx={{ color: "#d32f2f", fontSize: 13 }}>
                (*) campos requeridos
              </Typography>
              <Button
                variant="contained"
                onClick={saveReferencia}
                sx={{
                  backgroundColor: UI_PRIMARY,
                  color: "#fff",
                  borderRadius: 2.2,
                }}
              >
                Guardar
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openPadecimiento}
            onClose={() => setOpenPadecimiento(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle sx={{ pb: 1 }}>
              {editPadecimientoId
                ? "Editar Padecimiento"
                : "Agregar Padecimiento"}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: "grid", gap: 1, pt: 0.5 }}>
                <FormControl size="small" error={Boolean(padecimientoError)}>
                  <InputLabel>(*) Padecimiento</InputLabel>
                  <Select
                    label="(*) Padecimiento"
                    value={padecimientoForm.id}
                    onChange={(e) =>
                      setPadecimientoForm((prev) => ({
                        ...prev,
                        id: e.target.value,
                        descripcion: getCatalogLabel(
                          catalogos.padecimientos,
                          "PadecimientoMedicoId",
                          "Descripcion",
                          e.target.value,
                        ),
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {(catalogos.padecimientos || []).map((item) => (
                      <MenuItem
                        key={`pad-opt-${item?.PadecimientoMedicoId}`}
                        value={String(item?.PadecimientoMedicoId || "").trim()}
                      >
                        {item?.Descripcion || ""}
                      </MenuItem>
                    ))}
                  </Select>
                  {padecimientoError ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "#d32f2f", ml: 1 }}
                    >
                      {padecimientoError}
                    </Typography>
                  ) : null}
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "space-between", px: 2, pb: 1.5 }}
            >
              <Typography sx={{ color: "#d32f2f", fontSize: 13 }}>
                (*) campos requeridos
              </Typography>
              <Button
                variant="contained"
                onClick={savePadecimiento}
                sx={{
                  backgroundColor: UI_PRIMARY,
                  color: "#fff",
                  borderRadius: 2.2,
                }}
              >
                Guardar
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openFamiliar}
            onClose={() => setOpenFamiliar(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle sx={{ pb: 1 }}>
              {editFamiliarId ? "Editar Familiar" : "Agregar Familiar"}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: "grid", gap: 1, pt: 0.5 }}>
                <TextField
                  size="small"
                  label="(*) Primer Nombre"
                  value={familiarForm.primerNombre}
                  onChange={(e) =>
                    setFamiliarForm((prev) => ({
                      ...prev,
                      primerNombre: e.target.value.replace(_ONLY_LETTERS, "").toUpperCase(),
                    }))
                  }
                  error={Boolean(familiarErrors.primerNombre)}
                  helperText={familiarErrors.primerNombre || ""}
                />
                <TextField
                  size="small"
                  label="Segundo Nombre"
                  value={familiarForm.segundoNombre}
                  onChange={(e) =>
                    setFamiliarForm((prev) => ({
                      ...prev,
                      segundoNombre: e.target.value.replace(_ONLY_LETTERS, "").toUpperCase(),
                    }))
                  }
                />
                <TextField
                  size="small"
                  label="(*) Primer Apellido"
                  value={familiarForm.primerApellido}
                  onChange={(e) =>
                    setFamiliarForm((prev) => ({
                      ...prev,
                      primerApellido: e.target.value.replace(_ONLY_LETTERS, "").toUpperCase(),
                    }))
                  }
                  error={Boolean(familiarErrors.primerApellido)}
                  helperText={familiarErrors.primerApellido || ""}
                />
                <TextField
                  size="small"
                  label="Segundo Apellido"
                  value={familiarForm.segundoApellido}
                  onChange={(e) =>
                    setFamiliarForm((prev) => ({
                      ...prev,
                      segundoApellido: e.target.value.replace(_ONLY_LETTERS, "").toUpperCase(),
                    }))
                  }
                />
                <FormControl
                  size="small"
                  error={Boolean(familiarErrors.parentesco)}
                >
                  <InputLabel>(*) Parentesco</InputLabel>
                  <Select
                    label="(*) Parentesco"
                    value={familiarForm.parentesco}
                    onChange={(e) =>
                      setFamiliarForm((prev) => ({
                        ...prev,
                        parentesco: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {(catalogos.parentescos || []).map((item) => (
                      <MenuItem
                        key={`par-${item?.ParentescoId}`}
                        value={String(item?.ParentescoId || "").trim()}
                      >
                        {item?.Descripcion || ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  error={Boolean(familiarErrors.nacionalidad)}
                >
                  <InputLabel>(*) Nacionalidad</InputLabel>
                  <Select
                    label="(*) Nacionalidad"
                    value={familiarForm.nacionalidad}
                    onChange={(e) =>
                      setFamiliarForm((prev) => ({
                        ...prev,
                        nacionalidad: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {(catalogos.nacionalidades || []).map((item) => (
                      <MenuItem
                        key={`nac-fam-${item?.NacionalidadId}`}
                        value={String(item?.NacionalidadId || "").trim()}
                      >
                        {item?.GentilicioDisplay || item?.Descripcion || ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "space-between", px: 2, pb: 1.5 }}
            >
              <Typography sx={{ color: "#d32f2f", fontSize: 13 }}>
                (*) campos requeridos
              </Typography>
              <Button
                variant="contained"
                onClick={saveFamiliar}
                sx={{
                  backgroundColor: UI_PRIMARY,
                  color: "#fff",
                  borderRadius: 2.2,
                }}
              >
                Guardar
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openEmpresa}
            onClose={() => setOpenEmpresa(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle sx={{ pb: 1 }}>
              {editEmpresaId ? "Editar Empresa" : "Agregar Empresa"}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: "grid", gap: 1, pt: 0.5 }}>
                <TextField
                  size="small"
                  label="(*) Razón social"
                  value={empresaForm.razonSocial}
                  onChange={(e) =>
                    setEmpresaForm((prev) => ({
                      ...prev,
                      razonSocial: e.target.value.toUpperCase(),
                    }))
                  }
                  error={Boolean(empresaErrors.razonSocial)}
                  helperText={empresaErrors.razonSocial || ""}
                />
                <TextField
                  size="small"
                  label="(*) RTN"
                  value={empresaForm.rtn}
                  onChange={(e) =>
                    setEmpresaForm((prev) => ({ ...prev, rtn: e.target.value.replace(_ONLY_DIGITS, "") }))
                  }
                  inputMode="numeric"
                  error={Boolean(empresaErrors.rtn)}
                  helperText={empresaErrors.rtn || ""}
                />
                <FormControl size="small" error={Boolean(empresaErrors.pais)}>
                  <InputLabel>(*) País</InputLabel>
                  <Select
                    label="(*) País"
                    value={empresaForm.pais}
                    onChange={(e) =>
                      setEmpresaForm((prev) => ({
                        ...prev,
                        pais: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {(catalogos.nacionalidades || []).map((item) => (
                      <MenuItem
                        key={`nac-emp-${item?.NacionalidadId}`}
                        value={String(item?.NacionalidadId || "").trim()}
                      >
                        {item?.Descripcion || ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  error={Boolean(empresaErrors.giroEntidad)}
                >
                  <InputLabel>(*) Giro de la entidad</InputLabel>
                  <Select
                    label="(*) Giro de la entidad"
                    value={empresaForm.giroEntidad}
                    onChange={(e) =>
                      setEmpresaForm((prev) => ({
                        ...prev,
                        giroEntidad: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {(catalogos.girosEntidad || []).map((item) => (
                      <MenuItem
                        key={`giro-emp-${item?.GiroEntidadId}`}
                        value={String(item?.GiroEntidadId || "").trim()}
                      >
                        {item?.Descripcion || ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "space-between", px: 2, pb: 1.5 }}
            >
              <Typography sx={{ color: "#d32f2f", fontSize: 13 }}>
                (*) campos requeridos
              </Typography>
              <Button
                variant="contained"
                onClick={saveEmpresa}
                sx={{
                  backgroundColor: UI_PRIMARY,
                  color: "#fff",
                  borderRadius: 2.2,
                }}
              >
                Guardar
              </Button>
            </DialogActions>
          </Dialog>

          {/* ── Diálogo cámara para actualizar fotografía ── */}
          <Dialog
            open={cameraDialogOpen}
            onClose={() => setCameraDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CameraAltIcon sx={{ color: "#19498c" }} />
              Tomar Fotografía
            </DialogTitle>
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                pt: "12px !important",
              }}
            >
              <Box
                sx={{
                  width: 260,
                  height: 340,
                  border: "1px solid #bdbdbd",
                  background: "#111",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                }}
              >
                {cameraOpen ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : capturedPhotoUrl ? (
                  <img
                    src={capturedPhotoUrl}
                    alt="Foto capturada"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <PersonIcon sx={{ fontSize: 120, color: "#555" }} />
                )}
              </Box>
              {cameraError && (
                <Alert severity="error" sx={{ width: "100%", fontSize: 12 }}>
                  {cameraError}
                </Alert>
              )}
              {fotoError && !cameraError && (
                <Alert severity="error" sx={{ width: "100%", fontSize: 12 }}>
                  {fotoError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "center", gap: 1, pb: 2, flexWrap: "wrap" }}
            >
              {cameraOpen && (
                <Button
                  variant="contained"
                  startIcon={<CameraAltIcon />}
                  onClick={captureActualizarPhoto}
                  sx={{
                    backgroundColor: "#19498c",
                    "&:hover": { backgroundColor: "#143f79" },
                  }}
                >
                  Capturar Foto
                </Button>
              )}
              {!cameraOpen && capturedPhotoUrl && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    onClick={() => {
                      setCapturedPhotoUrl("");
                      openActualizarCamera();
                    }}
                  >
                    Volver a tomar
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={uploadingFoto}
                    onClick={handleSaveCapturedPhoto}
                  >
                    {uploadingFoto ? "Guardando..." : "Guardar Foto"}
                  </Button>
                </>
              )}
              <Button
                onClick={() => setCameraDialogOpen(false)}
                color="inherit"
              >
                {capturedPhotoUrl ? "Cancelar" : "Cerrar"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
